import {
  RagAlternativeSource,
  RagImageItem,
  RagProcedure,
  RagProcedureStep,
  RagProcedureVideo,
  RagVideoItem,
} from "../types/ragChatTypes";

/** Normalize PDF identifiers for set comparisons (case/path agnostic). */
export function normalizePdfKey(name: string): string {
  return (name || "").trim().toLowerCase().replace(/\.pdf$/i, "");
}

/**
 * Human-readable video filename from a worker path or signed playback URL.
 * Strips query params (e.g. ?token=) and URL-decodes %20 etc.
 */
export function videoDisplayName(pathOrUrl: string, fallback = "Video"): string {
  const withoutQuery = (pathOrUrl || "").split("?")[0] ?? "";
  const segment =
    withoutQuery.replace(/\\/g, "/").split("/").pop()?.trim() || fallback;
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

export type SourceMediaKind = "pdf" | "video" | "other";

const VIDEO_EXTENSIONS = new Set([
  ".mp4",
  ".mov",
  ".avi",
  ".mkv",
  ".webm",
  ".m4v",
]);

function fileExtension(fileName: string): string {
  const base =
    (fileName || "").split("?")[0].replace(/\\/g, "/").split("/").pop() || "";
  const dot = base.lastIndexOf(".");
  return dot >= 0 ? base.slice(dot).toLowerCase() : "";
}

/**
 * Classify a source/document label for icon + click behavior.
 * Prefers backend `type` when present; otherwise uses filename extension.
 */
export function sourceMediaKind(
  fileName: string,
  type?: string | null
): SourceMediaKind {
  const t = (type || "").trim().toLowerCase();
  if (t === "video") return "video";
  if (t === "pdf" || t === "text") {
    const ext = fileExtension(fileName);
    if (VIDEO_EXTENSIONS.has(ext)) return "video";
    return "pdf";
  }

  const ext = fileExtension(fileName);
  if (VIDEO_EXTENSIONS.has(ext)) return "video";
  if (ext === ".pdf") return "pdf";
  return "other";
}

/**
 * PDFs listed in alternativePdfs that are not already covered by alternativeSources.
 * When every alternative PDF has a matching source card, returns [] so "Also available in"
 * can be hidden without hardcoding document names.
 */
export function alternativePdfsNotInSources(
  pdfs: string[] | undefined,
  sources: RagAlternativeSource[] | undefined
): string[] {
  const sourceKeys = new Set(
    (sources ?? []).flatMap((s) =>
      [s.pdf_name, s.source_label].filter(Boolean).map((n) => normalizePdfKey(String(n)))
    )
  );
  return (pdfs ?? []).filter((pdf) => pdf.trim() && !sourceKeys.has(normalizePdfKey(pdf)));
}

export interface ParsedMarkdownTable {
  headers: string[];
  rows: string[][];
}

/** Converts a GFM markdown table string into headers + rows, or null if invalid. */
export function parseMarkdownTable(md: string): ParsedMarkdownTable | null {
  const lines = md
    .trim()
    .split("\n")
    .filter((l) => l.trim());
  if (lines.length < 3) return null;

  const parseRow = (line: string) =>
    line
      .replace(/^\|/, "")
      .replace(/\|$/, "")
      .split("|")
      .map((c) => c.trim());

  const headers = parseRow(lines[0]);
  const isSep = lines[1].replace(/[\s|:-]/g, "").length === 0;
  if (!isSep) return null;

  const rows = lines.slice(2).map(parseRow);
  return { headers, rows };
}

export interface TextSegment {
  text: string;
  page: string | null;
}

/** Splits on [p.N] citation markers. */
export function parseSegments(text: string): TextSegment[] {
  const parts = text.split(/(\[p\.(\d+)\])/);
  const segments: TextSegment[] = [];
  for (let i = 0; i < parts.length; i++) {
    if (/^\[p\.\d+\]$/.test(parts[i])) {
      const page = parts[i + 1];
      if (segments.length > 0) {
        segments[segments.length - 1].page = page ?? null;
      } else {
        segments.push({ text: "", page: page ?? null });
      }
      i++;
    } else if (parts[i]) {
      segments.push({ text: parts[i], page: null });
    }
  }
  return segments;
}

export interface ParsedStep {
  num: string | null;
  text: string;
  marker: "dot" | "paren" | "dash" | "step" | null;
}

/** A visual group of numbered steps under an optional Markdown section heading. */
export interface ProcedureSection {
  /** Actual heading text from the answer; null when the group has no Markdown heading. */
  title: string | null;
  /** Indices into the full ParsedStep[] (including non-numbered entries). */
  stepIndices: number[];
}

type StepMarker = NonNullable<ParsedStep["marker"]>;

const STEP_LINE = /^\s*(\d+)([.)]|\s+-)\s+(.*)/i;
const STEP_WORD = /^\s*step\s+(\d+)[.:]\s*(.*)/i;

/** Standalone Markdown bold / ATX heading used as a procedure section boundary. */
const MARKDOWN_SECTION_HEADING_RE =
  /^(?:\*\*([^*]+)\*\*|__([^_]+)__|#{1,6}\s+(.+?))\s*:?\s*$/;

function markerFromToken(token: string): StepMarker {
  if (token === ".") return "dot";
  if (token === ")") return "paren";
  if (token.trim() === "-") return "dash";
  return "step";
}

export interface PackedNumberedStepSegment {
  num: string;
  marker: StepMarker;
  text: string;
}

/**
 * Split a single physical line that packs multiple numbered instructions
 * (e.g. `1. Do A. 2. Do B. 3. Do C.`) into separate segments.
 *
 * Returns null when the line is ambiguous or should be left unchanged —
 * decimals, page refs, prose, and non-consecutive numbers do not unpack.
 */
export function splitPackedNumberedStepSegments(
  line: string
): PackedNumberedStepSegment[] | null {
  const raw = String(line ?? "").trim();
  if (!raw) return null;

  // Start-or-whitespace + N. / N) / N - + whitespace (same shapes as STEP_LINE).
  const markerRe = /(?:^|\s)(\d+)([.)]|\s+-)\s+/g;
  type Cand = {
    digitIndex: number;
    contentStart: number;
    num: number;
    markerToken: string;
  };
  const cands: Cand[] = [];

  let m: RegExpExecArray | null;
  while ((m = markerRe.exec(raw)) !== null) {
    const full = m[0]!;
    const numStr = m[1]!;
    const markerToken = m[2]!;
    const leadingWs = full.match(/^\s*/)?.[0].length ?? 0;
    const digitIndex = m.index + leadingWs;

    // Never treat a digit glued to a letter/digit as a step marker.
    if (digitIndex > 0 && /[A-Za-z0-9./]/.test(raw[digitIndex - 1] ?? "")) {
      continue;
    }

    const after = raw.slice(m.index + full.length);
    if (!after.trim()) continue;
    // Plausible instruction / spec / label body (reject lowercase prose tails).
    if (!/^[A-Z0-9("'[*•\-]/.test(after)) continue;

    cands.push({
      digitIndex,
      contentStart: m.index + full.length,
      num: Number.parseInt(numStr, 10),
      markerToken,
    });
  }

  if (cands.length < 2) return null;
  // Only unpack lines that themselves begin with a numbered marker.
  if (cands[0]!.digitIndex !== 0) return null;

  for (let i = 1; i < cands.length; i++) {
    if (cands[i]!.num !== cands[0]!.num + i) return null;
  }

  const segments: PackedNumberedStepSegment[] = [];
  for (let i = 0; i < cands.length; i++) {
    const c = cands[i]!;
    const textEnd = i + 1 < cands.length ? cands[i + 1]!.digitIndex : raw.length;
    const text = raw.slice(c.contentStart, textEnd).trim();
    if (!text) return null;
    segments.push({
      num: String(c.num),
      marker: markerFromToken(c.markerToken),
      text,
    });
  }
  return segments;
}

/** True when a line is a Warnings/Notes section heading (not a procedure section). */
export function isWarningsNotesHeading(line: string): boolean {
  const cleaned = stripInlineMarkdown(String(line ?? "").trim())
    .replace(/:+$/, "")
    .trim();
  return /^warnings?\s*(?:and|\/|&)\s*notes?$/i.test(cleaned);
}

/**
 * Normalize a candidate video-source provenance line for classification.
 */
function normalizeVideoSourceNoticeText(text: string): string {
  return stripInlineMarkdown(String(text ?? "").trim())
    .replace(/^#{1,6}\s+/, "")
    .replace(/:+$/, "")
    .trim();
}

/**
 * Shared semantic classifier for video-derived provenance / fallback preambles
 * from RAG synthesizers. Informational context — NEVER a procedure section heading.
 *
 * Covers:
 * 1. Procedure synthesizer — "No PDF manual procedure was found... matching videos..."
 *    → informational callout + Step Cards
 * 2. Diagnostic / normal-answer synthesizer — "No PDF manual was found... drawn from
 *    the available video..." → informational callout + normal answer (not Step Cards)
 */
export function isVideoSourceProvenanceNotice(text: string): boolean {
  const cleaned = normalizeVideoSourceNoticeText(text);
  if (!cleaned) return false;
  if (!/\bno\s+pdf\b/i.test(cleaned)) return false;
  if (!/\bvideos?\b/i.test(cleaned)) return false;
  // Procedure fallback (Step Cards follow).
  if (/\bprocedure\b/i.test(cleaned)) return true;
  // Normal / diagnostic answer drawn from video.
  if (/\bdrawn\s+from\b/i.test(cleaned)) return true;
  if (/\bno\s+pdf\s+manual\b/i.test(cleaned)) return true;
  return false;
}

/**
 * Alias kept for existing call sites — same semantic family as
 * {@link isVideoSourceProvenanceNotice}.
 */
export function isVideoPrimaryProcedureNotice(text: string): boolean {
  return isVideoSourceProvenanceNotice(text);
}

/**
 * Video-only procedure synthesizer notice specifically
 * ("No PDF manual procedure was found... matching videos below").
 * Distinct from diagnostic "drawn from the available video" answers.
 */
export function isVideoPrimaryProcedureFallbackNotice(text: string): boolean {
  const cleaned = normalizeVideoSourceNoticeText(text);
  if (!cleaned) return false;
  return (
    /\bno\s+pdf\b/i.test(cleaned) &&
    /\bprocedure\b/i.test(cleaned) &&
    /\bvideos?\b/i.test(cleaned)
  );
}

/**
 * Video-primary procedure fallback: provenance notice + assembled numbered
 * steps. Mirrors backend procedure_builder video_primary bypass — title-style
 * steps (e.g. "1. Preparation: Loosen…") must still use Step Cards even when
 * catalog heuristics would classify them as spec-list.
 */
export function isVideoPrimaryProcedureFallback(steps: ParsedStep[]): boolean {
  const hasNotice = steps.some(
    (s) => !s.num && isVideoPrimaryProcedureFallbackNotice(s.text)
  );
  if (!hasNotice) return false;
  return steps.filter((s) => s.num).length >= 2;
}

/** Clean display text for a video-source notice from parsed steps, or null. */
export function extractVideoPrimaryProcedureNotice(
  steps: ParsedStep[]
): string | null {
  for (const step of steps) {
    if (step.num) continue;
    if (!isVideoSourceProvenanceNotice(step.text)) continue;
    const cleaned = stripInlineMarkdown(cleanStepDisplayText(step.text)).trim();
    return cleaned || null;
  }
  return null;
}

/**
 * Split a prose answer so the leading video-source provenance sentence becomes
 * an informational callout, with the remaining body rendered as a normal answer.
 * Does not convert the body into Step Cards.
 */
export function splitVideoSourceProvenanceNotice(text: string): {
  notice: string | null;
  body: string;
} {
  const normalized = String(text ?? "").replace(/\r\n/g, "\n");
  const lines = normalized.split("\n");
  let notice: string | null = null;
  const bodyLines: string[] = [];
  let startedBody = false;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!startedBody && !notice) {
      if (!trimmed) continue;
      if (isVideoSourceProvenanceNotice(trimmed)) {
        notice =
          stripInlineMarkdown(cleanStepDisplayText(trimmed)).trim() || null;
        continue;
      }
      startedBody = true;
      bodyLines.push(line);
      continue;
    }
    startedBody = true;
    bodyLines.push(line);
  }

  while (bodyLines.length && !bodyLines[0]!.trim()) bodyLines.shift();
  while (bodyLines.length && !bodyLines[bodyLines.length - 1]!.trim()) {
    bodyLines.pop();
  }

  return { notice, body: bodyLines.join("\n") };
}

/**
 * Untitled procedures normally show a standalone "N steps" / topic heading.
 * Video-source provenance fallbacks use the informational callout instead —
 * never that heading. Named sections keep their badge + count via hasTitledSections.
 */
export function shouldShowStandaloneProcedureStepHeading(
  hasTitledSections: boolean,
  videoPrimaryNotice: string | null | undefined
): boolean {
  if (hasTitledSections) return false;
  if (videoPrimaryNotice) return false;
  return true;
}

/** True when the line is a Markdown ATX heading (`#`–`######`). */
export function isAtxMarkdownHeading(line: string): boolean {
  return /^\s*#{1,6}\s+\S/.test(String(line ?? ""));
}

/**
 * True when a line is a standalone Markdown section heading
 * (e.g. `**Post-Install**`, `## Installation`, `#### Video 2`),
 * not inline bold inside a step.
 */
export function isMarkdownSectionHeading(line: string): boolean {
  const trimmed = String(line ?? "").trim();
  if (!trimmed) return false;
  if (STEP_LINE.test(trimmed) || STEP_WORD.test(trimmed)) return false;
  if (isWarningsNotesHeading(trimmed)) return true;
  if (isVideoSourceProvenanceNotice(trimmed)) return false;

  const match = trimmed.match(MARKDOWN_SECTION_HEADING_RE);
  if (!match) return false;

  const title = (match[1] ?? match[2] ?? match[3] ?? "").replace(/\*+/g, "").trim();
  if (!title || title.length > 80) return false;
  // Reject lines that are mostly punctuation / markers.
  if (!/[A-Za-z]/.test(title)) return false;
  if (isVideoSourceProvenanceNotice(title)) return false;
  return true;
}

function sectionHeadingDisplayText(line: string): string {
  const trimmed = String(line ?? "").trim();
  const match = trimmed.match(MARKDOWN_SECTION_HEADING_RE);
  if (match) {
    return (match[1] ?? match[2] ?? match[3] ?? "").replace(/\*+/g, "").trim();
  }
  return stripInlineMarkdown(trimmed).replace(/:+$/, "").trim();
}

function isStepContinuationLine(line: string): boolean {
  if (!String(line ?? "").trim()) return false;
  if (/^[ \t]/.test(line)) return true;
  return /^[-–—*•▸◆]\s+\S/.test(line.trim());
}

export function parseNumberedSteps(text: string): ParsedStep[] | null {
  const lines = text.split("\n");
  const steps: ParsedStep[] = [];
  let current: ParsedStep | null = null;
  let blankPending = false;

  for (const line of lines) {
    const trimmed = line.trim();

    // Warnings/Notes ends procedure content — never fold into the previous step.
    if (trimmed && isWarningsNotesHeading(trimmed)) {
      if (current) {
        steps.push(current);
        current = null;
      }
      break;
    }

    // Mid-procedure Markdown headings are section boundaries, not step body text.
    if (trimmed && isMarkdownSectionHeading(trimmed)) {
      if (current) {
        steps.push(current);
        current = null;
      }
      steps.push({
        num: null,
        text: sectionHeadingDisplayText(trimmed),
        marker: null,
      });
      continue;
    }

    const m1 = line.match(STEP_LINE);
    const m2 = !m1 ? line.match(STEP_WORD) : null;
    if (!trimmed) {
      blankPending = Boolean(current);
      continue;
    }
    if (blankPending && current && !m1 && !m2 && !isStepContinuationLine(line)) {
      steps.push(current);
      current = null;
    }
    blankPending = false;
    if (m1) {
      if (current) steps.push(current);
      const packed = splitPackedNumberedStepSegments(trimmed);
      if (packed && packed.length >= 2) {
        for (const seg of packed) {
          steps.push({
            num: seg.num,
            text: seg.text,
            marker: seg.marker,
          });
        }
        current = null;
      } else {
        current = {
          num: m1[1],
          text: m1[3] ?? "",
          marker: markerFromToken(m1[2] ?? ""),
        };
      }
    } else if (m2) {
      if (current) steps.push(current);
      current = {
        num: m2[1],
        text: m2[2] ?? "",
        marker: "step",
      };
    } else if (current) {
      current.text += ` ${trimmed}`;
    } else {
      steps.push({ num: null, text: line, marker: null });
    }
  }
  if (current) steps.push(current);

  const numbered = steps.filter((s) => s.num);
  if (numbered.length === 0) return null;
  return steps;
}

/**
 * Group numbered steps under Markdown section headings from the answer.
 * Leading headings (e.g. `**Removing Front Bumper**`) become the first section title.
 * Later headings (e.g. `**Post-Install**`) start subsequent sections.
 * Original step numbers are never renumbered.
 * Headings with no following steps are dropped.
 */
export function groupProcedureSections(steps: ParsedStep[]): ProcedureSection[] {
  const sections: ProcedureSection[] = [];
  let current: ProcedureSection = { title: null, stepIndices: [] };

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]!;
    if (step.num) {
      current.stepIndices.push(i);
      continue;
    }

    const title = stripInlineMarkdown(cleanStepDisplayText(step.text))
      .replace(/:+$/, "")
      .trim();
    if (!title) continue;
    if (isWarningsNotesHeading(title)) break;
    // Video-primary preamble is informational context, not a section badge.
    if (isVideoSourceProvenanceNotice(title)) continue;

    if (current.stepIndices.length > 0) {
      sections.push(current);
      current = { title, stepIndices: [] };
      continue;
    }

    // Heading before any steps in this group — becomes this section's title.
    current.title = title;
  }

  if (current.stepIndices.length > 0) {
    sections.push(current);
  }

  return sections;
}

/** Compact informational card vs horizontal Step Card carousel. */
export type ProcedureBlockKind = "compact" | "steps";

/** Visual variant for compact procedure section cards. */
export type ProcedureCompactVariant = "prose" | "tools" | "safety" | "documents";

export type StepVideoBinding = { videoNum: number; item: RagVideoItem };

export interface ProcedureBlockItem {
  /** Display number for Step Cards (1-based string); null for unnumbered compact bullets. */
  num: string | null;
  text: string;
  /** Clips bound to this card (from the worker `procedure` payload). */
  videos?: StepVideoBinding[];
}

/** One top-level work-instruction section (Purpose, Tools, Steps, …). */
export interface ProcedureBlock {
  title: string;
  kind: ProcedureBlockKind;
  compactVariant?: ProcedureCompactVariant;
  /** Prose / leftover text for compact cards. */
  body: string;
  /** Nested numbered steps or bullets (Step Cards or compact lists). */
  items: ProcedureBlockItem[];
  /**
   * ATX group this section belongs to (`#`–`######`).
   * Empty ATX headings label following WI sections until the next ATX.
   */
  groupTitle?: string | null;
  /**
   * Nested empty ATX under a group that still has no children
   * (e.g. `### Before starting` then `### Battery Health Check Procedure`).
   */
  nestedTitle?: string | null;
}

const WI_SECTION_LINE_RE = /^\s*(\d+)[.)]\s+(.*)$/;
const WI_BULLET_LINE_RE = /^\s*[-*•]\s+(.*)$/;

const ACTIONABLE_STEP_ITEM_RE =
  /\b(remove|install|attach|disconnect|connect|loosen|tighten|bleed|check|inspect|measure|replace|apply|coat|lubricate|secure|drive|use|open|close|press|turn|lift|lower|align|insert|pull|push|clean|wipe|drain|refill|start|verify|confirm|ensure|dispose|reinstall|torque|test|perform|follow|place|position|wait|record|compare)\b/i;

function normalizeWiTitle(raw: string): string {
  return stripInlineMarkdown(String(raw ?? ""))
    .replace(/:+$/, "")
    .trim();
}

/** Title families that always stay compact (never Step Card carousels). */
export function isToolsSectionTitle(title: string): boolean {
  const t = normalizeWiTitle(title).replace(/^[-*•]\s+/, "");
  // Heading-shaped only — body lines that merely mention "tools" must not match.
  return /^(?:tools?(?:\s*(?:[&/]|and)\s*materials?)?(?:\s+required)?|materials?(?:\s+required)?|required\s+(?:tools?|materials?|equipment|parts?)|equipment(?:\s+required)?|consumables?|parts?\s+required)\b/i.test(
    t
  );
}

export function isSafetySectionTitle(title: string): boolean {
  const t = normalizeWiTitle(title).replace(/^[-*•]\s+/, "");
  if (isWarningsNotesHeading(t)) return false;
  // Heading-shaped only — "Safety gloves…" must not match.
  return /^(?:safety(?:\s+(?:precautions?|notices?|information|instructions?))?|precautions?|cautions?)$/i.test(
    t
  );
}

/**
 * Bare post-step caution / prohibition headings (e.g. "Do Not", "Never").
 * Distinct from imperative lines ("Do not remove the radiator cap…") and from
 * the top-rail "Safety Precautions" section.
 */
export function isStandaloneCautionSectionTitle(title: string): boolean {
  const t = normalizeWiTitle(title).replace(/^[-*•]\s+/, "");
  if (!t || isWarningsNotesHeading(t)) return false;
  return /^(?:do\s+not|don'?t|never|important|warning|prohibitions?)$/i.test(t);
}

export function isDocumentsSectionTitle(title: string): boolean {
  const t = normalizeWiTitle(title);
  return /^(?:related\s+documents?|references?|see\s+also|documents?|cross[-\s]?references?)\b/i.test(
    t
  );
}

export function isProseLeadSectionTitle(title: string): boolean {
  const t = normalizeWiTitle(title);
  if (/^(purpose|overview|scope|introduction|summary)\b/i.test(t)) return true;
  // "Before you begin" and synthesizer variant "Before starting".
  if (/^before\s+(?:you\s+)?begin\b/i.test(t)) return true;
  if (/^before\s+starting\b/i.test(t)) return true;
  if (/^prerequisites?\b/i.test(t)) return true;
  if (/^when\s+to\s+use\b/i.test(t)) return true;
  return false;
}

/** Compact WI families (Purpose / Tools / Safety / Documents / cautions). */
export function isCompactFamilySectionTitle(title: string): boolean {
  return (
    isToolsSectionTitle(title) ||
    isSafetySectionTitle(title) ||
    isDocumentsSectionTitle(title) ||
    isProseLeadSectionTitle(title) ||
    isStandaloneCautionSectionTitle(title) ||
    isWarningsNotesHeading(title)
  );
}

/** Titles that suggest an actionable phase (steps / verification / removal…). */
export function isStepsPhaseSectionTitle(title: string): boolean {
  const t = normalizeWiTitle(title).replace(/\s*\([^)]*\)\s*$/g, "").trim();
  if (isToolsSectionTitle(t) || isSafetySectionTitle(t) || isDocumentsSectionTitle(t)) {
    return false;
  }
  if (isProseLeadSectionTitle(t)) return false;
  return /\b(steps?|procedure|verification|verif(?:y|ying)|inspection|inspect(?:ing)?|checks?|checking|tests?|testing|guide|guidance|assessment|evaluation|removal|removing|installation|installing|replac\w*|disassembl\w*|assembl\w*|diagnos\w*|repair\w*|service|servicing|operation|operations|actions?)\b/i.test(
    t
  );
}

/**
 * Structural WI section heading (e.g. "Purpose", "Replacement Steps") —
 * not an imperative action line ("Open the hood").
 */
export function isWorkInstructionSectionTitle(title: string): boolean {
  const t = normalizeWiTitle(title);
  if (!t || t.length > 80) return false;
  if (looksLikeImperativeProcedureLine(t)) return false;

  if (
    isToolsSectionTitle(t) ||
    isSafetySectionTitle(t) ||
    isDocumentsSectionTitle(t) ||
    isProseLeadSectionTitle(t) ||
    isWarningsNotesHeading(t) ||
    isStepsPhaseSectionTitle(t)
  ) {
    return true;
  }

  const words = t.split(/\s+/).filter(Boolean);
  if (words.length < 1 || words.length > 8) return false;
  if (/[.!?]$/.test(t)) return false;

  const titleish = words.filter(
    (w) =>
      /^[A-Z0-9]/.test(w) || /^(of|the|and|for|in|on|to|a|an|&|\/)$/i.test(w)
  ).length;
  return titleish >= Math.ceil(words.length * 0.7);
}

/**
 * Strict heading gate for starting a new WI section while scanning body lines.
 * Rejects bullets, sentences, and keyword-containing items
 * (e.g. "Avoid letting tools…", "Replacement battery…", "Verify accessories…").
 *
 * Accepts hierarchical diagnostic phase labels such as:
 * "Voltage Check", "Cranking Test", "Load Test (…)", "Decision Guide".
 */
function isStructuralWiHeading(title: string): boolean {
  const raw = String(title ?? "").trim();
  if (!raw || /^[-*•]\s+/.test(raw)) return false;

  const t = normalizeWiTitle(raw);
  if (!t || t.length > 80) return false;
  if (/[.!?]$/.test(t)) return false;
  if (looksLikeImperativeProcedureLine(t)) return false;

  const tCore = t.replace(/\s*\([^)]*\)\s*$/g, "").trim() || t;

  // Catalogued WI headings win before sentence-word rejects ("When to Use This Document").
  if (
    isStandaloneCautionSectionTitle(t) ||
    isSafetySectionTitle(t) ||
    isToolsSectionTitle(t) ||
    isDocumentsSectionTitle(t) ||
    isProseLeadSectionTitle(t) ||
    isWarningsNotesHeading(t) ||
    isToolsSectionTitle(tCore) ||
    isSafetySectionTitle(tCore) ||
    isDocumentsSectionTitle(tCore) ||
    isProseLeadSectionTitle(tCore) ||
    isWarningsNotesHeading(tCore)
  ) {
    return true;
  }

  if (
    /^(?:verify|confirm|ensure|avoid|wear|do\s+not|make\s+sure|start|dispose|always|never|failing|passing|applying|connecting|measuring|performing|checking|testing|during|if|when)\b/i.test(
      t
    )
  ) {
    return false;
  }
  // Decision-guide outcome lines ("Passes all checks…", "Fails load…", "Low voltage…").
  if (/^(?:passes?|fails?|failing|passing|low)\b/i.test(t)) {
    return false;
  }
  if (/[><]=?|\bholds\b|\brecovers\b|\breplace\b|\brecharge\b/i.test(t)) {
    return false;
  }
  // Sentence fragments / reference lines that merely mention a phase noun.
  if (
    /\b(this|that|these|those|with|from|into|under|over|than|then|while|because|which|where|should|cannot|can|will|would|must)\b/i.test(
      t
    )
  ) {
    return false;
  }
  // Catalog cross-refs ("WI-BATT-002 — Battery Health Check Procedure").
  if (/—|--/.test(t) || /\bWI-[A-Z0-9-]+/i.test(t) || /\b[A-Z]{2,5}-\d{2,}/i.test(t)) {
    return false;
  }

  const words = tCore.split(/\s+/).filter(Boolean);
  if (words.length < 1 || words.length > 8) return false;

  if (!isStepsPhaseSectionTitle(tCore) && !isStepsPhaseSectionTitle(t)) {
    return false;
  }

  // Phase headings end with a section noun ("… Test", "… Check", "… Guide").
  if (
    /\b(steps?|procedure|verification|inspection|checks?|tests?|guide|guidance|phase|actions?|assessment|evaluation)\s*$/i.test(
      tCore
    )
  ) {
    return true;
  }
  // Bare phase labels only — not "Replacement battery" / "Installation torque".
  if (
    /^(removal|installation|replacement|disassembly|assembly|diagnosis|repair|service|operation)(?:\s+(steps?|procedure|phase|process))?$/i.test(
      tCore
    )
  ) {
    return true;
  }
  return false;
}

/**
 * Used only when peeling unheaded numbered steps out of intro prose.
 * Not used to choose Step Cards vs compact layout.
 */
function looksLikeActionableStepItem(text: string): boolean {
  const clean = cleanStepDisplayText(stripInlineMarkdown(text)).trim();
  if (!clean) return false;
  if (looksLikeSpecificationLine(text)) return false;
  if (looksLikeImperativeProcedureLine(clean)) return true;
  if (ACTIONABLE_STEP_ITEM_RE.test(clean)) return true;
  // Verification / check phrasing without a leading verb list hit.
  if (/^(start|verify|confirm|ensure|dispose|check|inspect|test)\b/i.test(clean)) {
    return true;
  }
  return false;
}

/**
 * Fold unnumbered bullet items into the preceding numbered step so nested
 * lists (e.g. DTC codes under "4. Common codes…") stay in that step's body
 * instead of becoming separate Step Cards.
 */
function nestUnnumberedItemsIntoNumberedParents(
  items: ProcedureBlockItem[]
): ProcedureBlockItem[] {
  const nested: ProcedureBlockItem[] = [];
  for (const item of items) {
    const text = item.text.trim();
    if (!text) continue;
    if (item.num != null) {
      nested.push({ num: item.num, text });
      continue;
    }
    const parent = nested.length ? nested[nested.length - 1]! : null;
    if (parent?.num != null) {
      const bulletLine = /^[-*•]\s+/.test(text) ? text : `• ${text}`;
      parent.text = `${parent.text}\n${bulletLine}`.trim();
      continue;
    }
    nested.push({ num: null, text });
  }
  return nested;
}

/**
 * `Step 1: Visual Inspection` as a bare line (not `1. Title` / ATX / **bold**).
 * Nested under an open WI section these are body subheads, not new top-level sections.
 */
export function isBareStepPhaseHeading(line: string): boolean {
  const first = String(line ?? "").split(/\n/)[0]?.trim() ?? "";
  if (!first) return false;
  if (WI_SECTION_LINE_RE.test(first)) return false;
  if (isMarkdownSectionHeading(first)) return false;
  const plain = stripInlineMarkdown(first).replace(/:+$/, "").trim();
  return /^Step\s+\d+\s*[:.)\-–—]\s+\S/i.test(plain);
}

function extractBodyItems(bodyText: string): {
  prose: string;
  items: ProcedureBlockItem[];
} {
  const lines = String(bodyText ?? "").replace(/\r\n/g, "\n").split("\n");
  const proseLines: string[] = [];
  const items: ProcedureBlockItem[] = [];
  let current: ProcedureBlockItem | null = null;
  let seenItem = false;

  const pushCurrent = () => {
    if (current) {
      current.text = current.text.trim();
      if (current.text) items.push(current);
      current = null;
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      if (current && isBareStepPhaseHeading(current.text)) {
        current.text += "\n";
      }
      continue;
    }

    if (isBareStepPhaseHeading(trimmed)) {
      pushCurrent();
      seenItem = true;
      current = { num: null, text: trimmed };
      continue;
    }

    const numbered = trimmed.match(WI_SECTION_LINE_RE);
    if (numbered) {
      // Nested action steps — never treat as a new top-level WI heading here.
      const packed = splitPackedNumberedStepSegments(trimmed);
      if (packed && packed.length >= 2) {
        pushCurrent();
        seenItem = true;
        for (const seg of packed) {
          items.push({ num: seg.num, text: seg.text });
        }
        current = null;
        continue;
      }
      const rest = numbered[2] ?? "";
      pushCurrent();
      seenItem = true;
      current = { num: numbered[1]!, text: rest };
      continue;
    }

    const bullet = trimmed.match(WI_BULLET_LINE_RE);
    if (bullet) {
      const bulletText = (bullet[1] ?? "").trim();
      // Nest under the open numbered step (common codes, notes, …).
      if (current?.num != null && bulletText) {
        current.text = `${current.text}\n• ${bulletText}`.trim();
        continue;
      }
      // Nest under an open `Step N:` subhead so later phases are not glued on.
      if (current && isBareStepPhaseHeading(current.text) && bulletText) {
        current.text = `${current.text}\n- ${bulletText}`.trim();
        continue;
      }
      pushCurrent();
      seenItem = true;
      current = { num: null, text: bulletText };
      continue;
    }

    if (current) {
      const sep = isBareStepPhaseHeading(current.text)
        ? "\n"
        : current.text
          ? " "
          : "";
      current.text += sep + trimmed;
    } else if (!seenItem) {
      proseLines.push(trimmed);
    } else {
      // Trailing prose after items — keep on last item when present.
      if (items.length) {
        const last = items[items.length - 1]!;
        last.text = `${last.text} ${trimmed}`.trim();
      } else {
        proseLines.push(trimmed);
      }
    }
  }
  pushCurrent();

  return {
    prose: proseLines.join("\n\n").trim(),
    items: nestUnnumberedItemsIntoNumberedParents(items),
  };
}

/**
 * Classify one WI section as compact card vs Step Card carousel.
 * Layout follows markdown list shape only — not English verb / title heuristics.
 *
 * - Nested numbered items → Step Cards (source numbers kept)
 * - Bullets / prose only → compact list or prose card
 * Heading-role titles (Tools, Safety, Documents, Purpose, …) stay compact.
 */
export function classifyProcedureBlock(
  title: string,
  body: string,
  items: ProcedureBlockItem[]
): Pick<ProcedureBlock, "kind" | "compactVariant" | "body" | "items"> {
  const prose = body.trim();
  const list = nestUnnumberedItemsIntoNumberedParents(
    items.map((it) => ({
      num: it.num,
      text: it.text.trim(),
    })).filter((it) => it.text)
  );

  if (isToolsSectionTitle(title)) {
    return { kind: "compact", compactVariant: "tools", body: prose, items: list };
  }
  // Keep Warnings and Notes on the procedure canvas (video citations live here).
  // Do not use compactVariant "safety" — that variant is reserved for the top rail.
  if (isWarningsNotesHeading(title)) {
    return { kind: "compact", compactVariant: "prose", body: prose, items: list };
  }
  // Standalone "Do Not" / "Never" use the safety compact card but stay inline
  // (not the top Safety notices rail).
  if (isSafetySectionTitle(title) || isStandaloneCautionSectionTitle(title)) {
    return { kind: "compact", compactVariant: "safety", body: prose, items: list };
  }
  if (isDocumentsSectionTitle(title)) {
    return {
      kind: "compact",
      compactVariant: "documents",
      body: prose,
      items: list,
    };
  }
  if (isProseLeadSectionTitle(title)) {
    return { kind: "compact", compactVariant: "prose", body: prose, items: list };
  }

  const numbered = list.filter((it) => it.num != null);
  if (numbered.length >= 1) {
    return {
      kind: "steps",
      body: prose,
      items: numbered.map((it) => ({
        num: it.num as string,
        text: it.text,
      })),
    };
  }

  return { kind: "compact", compactVariant: "prose", body: prose, items: list };
}

export interface ParsedProcedureDocument {
  /** Leading prose before the first numbered WI section. */
  intro: string | null;
  /**
   * Empty leading non-ATX umbrella (e.g. `**Battery Health Check Procedure**`).
   * ATX titles stay on blocks as `groupTitle` so multiple ATX headings can render.
   */
  documentTitle: string | null;
  blocks: ProcedureBlock[];
}

export interface RawProcedureSection {
  title: string;
  body: string;
  sectionNum: number | null;
  /** Markdown ATX (`#`–`######`) vs WI `**bold**` / numbered heading. */
  headingKind?: "atx" | "wi";
  /** True when this section was peeled from unheaded numbered intro lines. */
  synthetic?: boolean;
}

/** Stable family key for answer-vs-alerts deduplication. */
export function procedureSectionKey(title: string): string {
  const t = normalizeWiTitle(title);
  if (isToolsSectionTitle(t)) return "tools";
  if (isDocumentsSectionTitle(t)) return "documents";
  if (isWarningsNotesHeading(t)) return "warnings-notes";
  // Post-step prohibition blocks stay distinct from Safety Precautions rail.
  if (isStandaloneCautionSectionTitle(t)) return "caution";
  // Safety precautions / safety notices — not Warnings and Notes (handled above).
  if (isSafetySectionTitle(t)) return "safety";
  if (/^purpose\b/i.test(t)) return "purpose";
  if (/^before\s+(?:you\s+)?begin\b/i.test(t) || /^before\s+starting\b/i.test(t)) {
    return "before-you-begin";
  }
  if (/^prerequisites?\b/i.test(t)) return "prerequisites";

  // Collapse equivalent step-phase titles ("Battery Replacement Steps" ≡ "Replacement Steps").
  if (isStepsPhaseSectionTitle(t)) {
    const lower = t.toLowerCase();
    if (/\bverif/.test(lower)) return "steps:verification";
    if (/\bpost[-\s]?install/.test(lower)) return "steps:post-install";
    if (/\breplac/.test(lower)) return "steps:replacement";
    if (/\bremov/.test(lower)) return "steps:removal";
    if (/\binstall/.test(lower)) return "steps:installation";
    if (/\binspect|\bchecks?\b|\bchecking\b/.test(lower)) return "steps:inspection";
    if (/\bdiagnos/.test(lower)) return "steps:diagnosis";
    if (/\bsteps?\b|\bprocedure\b|\bactions?\b/.test(lower)) return "steps:main";
    return `steps:${lower.replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;
  }

  return t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

/**
 * Strip a leading WI section index for classification / display:
 * `1. Title`, `2) Title`, `Step 1: Title`, `Step 2. Title`.
 */
function stripLeadingWiSectionNumber(title: string): {
  title: string;
  sectionNum: number | null;
} {
  const raw = normalizeWiTitle(title).replace(/\*+$/g, "").trim();
  if (!raw) return { title: raw, sectionNum: null };

  const numbered = raw.match(/^(\d+)[.)]\s+(.*)$/);
  if (numbered) {
    const sectionNum = Number.parseInt(numbered[1]!, 10);
    const rest = (numbered[2] ?? "").trim();
    if (!Number.isFinite(sectionNum) || !rest) {
      return { title: raw, sectionNum: null };
    }
    // `1. Step 1: Visual Inspection` → keep outer index, strip inner Step label.
    const inner = stripLeadingWiSectionNumber(rest);
    return {
      title: inner.title,
      sectionNum,
    };
  }

  const stepLabeled = raw.match(/^Step\s+(\d+)\s*[:.)\-–—]\s*(.+)$/i);
  if (stepLabeled) {
    const sectionNum = Number.parseInt(stepLabeled[1]!, 10);
    const rest = (stepLabeled[2] ?? "").trim();
    if (!Number.isFinite(sectionNum) || !rest) {
      return { title: raw, sectionNum: null };
    }
    return { title: rest, sectionNum };
  }

  return { title: raw, sectionNum: null };
}

/**
 * From text immediately after `N. ` / `N) `, take the longest prefix that is a
 * structural WI heading. Cuts before same-line `•` bullets and before a
 * following nested step number (`Replacement Steps 1. Open…`).
 */
function takeStructuralHeadingFromRest(rest: string): {
  title: string;
  consumed: number;
} | null {
  if (!rest) return null;

  let region = rest.replace(/^\*+\s*/, "");
  const bulletAt = region.search(/\s*[•]\s*/);
  if (bulletAt >= 0) {
    region = region.slice(0, bulletAt);
  }

  // Prefer `Step N: Phase Title` as a single heading unit (strip label later).
  const stepLabeled = region.match(
    /^(Step\s+\d+\s*[:.)\-–—]\s*.+?)(?=\s+\d+[.)]\s+|\s*[•]|$)/i
  );
  if (stepLabeled) {
    let candidate = stepLabeled[1]!.replace(/\*+$/g, "").trim();
    // Include a trailing parenthetical qualifier glued to the title.
    const afterStep = region.slice(stepLabeled[1]!.length);
    const paren = afterStep.match(/^\s*(\([^)]*\))/);
    if (paren && !/\([^)]*\)\s*$/.test(candidate)) {
      candidate = `${candidate} ${paren[1]}`.trim();
    }
    const stripped = stripLeadingWiSectionNumber(normalizeWiTitle(candidate));
    if (
      stripped.title &&
      isStructuralWiHeading(stripped.title) &&
      !isWarningsNotesHeading(stripped.title)
    ) {
      const escaped = candidate.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const m = rest.match(new RegExp(`^\\s*\\**\\s*${escaped}\\**`));
      if (m) {
        return { title: stripped.title, consumed: m[0].length };
      }
      // Fallback: consume the matched candidate span from region start.
      const consumed =
        (rest.match(/^\s*\**/)?.[0]?.length ?? 0) + candidate.length;
      return { title: stripped.title, consumed };
    }
  }

  // "Replacement Steps 1. Open the hood…" — title stops before nested steps.
  const nested = region.match(
    /^(.*?\b(?:steps?|procedure|verification|inspection|checks?|tests?|guide|guidance|precautions?|notices?|materials?|required|begin|starting|purpose|documents?|references?))\s+\d+[.)]\s+/i
  );
  if (nested) {
    region = nested[1]!;
  }

  // Count parenthetical qualifiers as one token so "Load Test (if …)" fits.
  const tokens: string[] = [];
  const tokenRe = /\([^)]*\)|[^\s]+/g;
  let tm: RegExpExecArray | null;
  while ((tm = tokenRe.exec(region.trim())) !== null) {
    tokens.push(tm[0]);
  }
  if (!tokens.length) return null;

  for (let n = Math.min(8, tokens.length); n >= 1; n--) {
    const candidate = tokens.slice(0, n).join(" ").replace(/\*+$/g, "").trim();
    const stripped = stripLeadingWiSectionNumber(normalizeWiTitle(candidate));
    const heading = stripped.title || candidate;
    if (isWarningsNotesHeading(heading)) continue;
    if (!isStructuralWiHeading(heading)) continue;

    const escaped = candidate.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const m = rest.match(new RegExp(`^\\s*\\**\\s*${escaped}`));
    if (!m) continue;
    return { title: heading, consumed: m[0].length };
  }

  return null;
}

/**
 * Rewrite aggregated alert blobs so the line-based WI parser can see sections.
 *
 * Handles mid-string headings and same-line bullets, e.g.:
 *   `… diagnostics. 3. Tools & Materials Required • item • item 4. Safety…`
 *
 * Only splits on numbered headings whose title is a validated structural WI
 * heading — never on arbitrary `1.` / `2.` procedure step numbers.
 */
export function normalizeAggregatedWiAlertText(text: string): string {
  const raw = String(text ?? "").replace(/\r\n/g, "\n");
  if (!raw.trim()) return raw;

  const headingStartRe = /(^|[^0-9])(\d+)[.)]\s+/g;
  type Break = { at: number; num: string; title: string; afterTitle: number };
  const breaks: Break[] = [];

  let m: RegExpExecArray | null;
  while ((m = headingStartRe.exec(raw)) !== null) {
    const numToken = m[2]!;
    // `at` is the start of the digits (skip the lookbehind-equivalent char).
    const at = m.index + m[1]!.length;
    const afterNum = m.index + m[0].length;
    const taken = takeStructuralHeadingFromRest(raw.slice(afterNum));
    if (!taken) continue;
    const after = raw
      .slice(afterNum + taken.consumed)
      .replace(/^[\s:—–\-*]+/, "")
      .trim();
    // Do not split "2. Do not remove…" into a false "Do Not" section break.
    if (isStandaloneCautionSectionTitle(taken.title) && after) continue;
    // Do not split "8. After … test the pedal…" mid-sentence.
    if (isLowercaseSentenceContinuation(after)) continue;
    breaks.push({
      at,
      num: numToken,
      title: taken.title,
      afterTitle: afterNum + taken.consumed,
    });
  }

  let rebuilt = raw;
  if (breaks.length) {
    const parts: string[] = [];
    let cursor = 0;
    for (const b of breaks) {
      // Skip matches that fall inside a previous section's consumed title span.
      if (b.at < cursor) continue;
      const before = raw.slice(cursor, b.at).replace(/\s+$/, "");
      if (before) parts.push(before);
      parts.push(`\n${b.num}. ${b.title}\n`);
      cursor = b.afterTitle;
    }
    const tail = raw.slice(cursor).replace(/^\s+/, "");
    if (tail) parts.push(tail);
    rebuilt = parts.join("");
  }

  // Put each bullet on its own line for extractBodyItems.
  rebuilt = rebuilt.replace(/\s*[•]\s+/g, "\n• ");
  // Normalize "- " bullets that were jammed mid-line after a heading.
  rebuilt = rebuilt.replace(/([^\n])\s+(-\s+)/g, "$1\n$2");

  return rebuilt.replace(/\n{3,}/g, "\n\n").trim();
}

/**
 * True when leftover text after a candidate heading continues the same sentence
 * (e.g. heading "… done, test" + body "the pedal for firmness…").
 * Legitimate same-line section bodies start with uppercase, bullets, or digits.
 */
function isLowercaseSentenceContinuation(inlineBody: string): boolean {
  const rest = String(inlineBody ?? "").trim();
  if (!rest) return false;
  // Nested lists / markers are real section bodies, not sentence tails.
  if (/^[-*•\d([{]/.test(rest)) return false;
  return /^[a-z]/.test(rest);
}

function tryStartWiSection(
  line: string
): {
  title: string;
  sectionNum: number | null;
  inlineBody: string;
  headingKind: "atx" | "wi";
} | null {
  const trimmed = String(line ?? "").trim();
  if (!trimmed) return null;

  const numbered = trimmed.match(WI_SECTION_LINE_RE);
  if (numbered) {
    const sectionNum = Number.parseInt(numbered[1]!, 10);
    const restRaw = numbered[2] ?? "";
    const taken = takeStructuralHeadingFromRest(restRaw);
    if (
      !Number.isFinite(sectionNum) ||
      !taken ||
      !isStructuralWiHeading(taken.title)
    ) {
      return null;
    }
    const inlineBody = restRaw
      .slice(taken.consumed)
      .replace(/^[\s:—–\-*]+/, "")
      .trim();
    // "2. Do not remove the radiator cap…" is a step, not a "Do Not" section.
    // Bare caution headings must stand alone on the line (or as **Do Not:**).
    if (isStandaloneCautionSectionTitle(taken.title) && inlineBody) {
      return null;
    }
    // "8. After … test the pedal…" — leftover lowercase means mid-sentence step text.
    if (isLowercaseSentenceContinuation(inlineBody)) {
      return null;
    }
    // Same-line instructional body is a titled step, not a section split.
    // Compact WI families (Purpose / Tools / Safety) may carry same-line prose.
    // Nested numbering on the leftover (`1. Connect…`) is a real child list.
    // Phase nouns ("Emissions Test") must not steal following 4. 5. 6. siblings.
    if (
      inlineBody &&
      !/^\d+[.)]\s+/.test(inlineBody) &&
      !isCompactFamilySectionTitle(taken.title)
    ) {
      return null;
    }
    return { title: taken.title, sectionNum, inlineBody, headingKind: "wi" };
  }

  if (isMarkdownSectionHeading(trimmed)) {
    // `**1. Purpose**` / `**Step 1: Visual Inspection**` → normalized title.
    const rawTitle = sectionHeadingDisplayText(trimmed);
    const stripped = stripLeadingWiSectionNumber(rawTitle);
    const title = normalizeWiTitle(stripped.title);
    if (!title) return null;
    const isAtx = isAtxMarkdownHeading(trimmed);
    // Dedicated heading lines (`# Title` or `**Title**`) start a section.
    // Compact vs Step Cards is decided later from the body list shape, not
    // from whether the title matches a WI catalog noun.
    return {
      title,
      sectionNum: stripped.sectionNum,
      inlineBody: "",
      headingKind: isAtx ? "atx" : "wi",
    };
  }

  // Plain title line used by many synthesizer answers ("Battery Replacement Steps:").
  if (/^[-*•]\s+/.test(trimmed)) return null;
  const stripped = stripLeadingWiSectionNumber(normalizeWiTitle(trimmed));
  const plainTitle = normalizeWiTitle(stripped.title);
  if (plainTitle && isStructuralWiHeading(plainTitle)) {
    return {
      title: plainTitle,
      sectionNum: stripped.sectionNum,
      inlineBody: "",
      headingKind: "wi",
    };
  }

  return null;
}

/** Numbered line that is an actionable instruction (not a document section heading). */
function isActionableNumberedStepLine(line: string): boolean {
  const trimmed = String(line ?? "").trim();
  const m = trimmed.match(/^(\d+)[.)]\s+(.+)$/);
  if (!m) return false;
  if (isNumberedDocumentSectionHeading(trimmed)) return false;
  const rest = (m[2] ?? "").trim();
  return looksLikeActionableStepItem(rest) || looksLikeImperativeProcedureLine(rest);
}

/**
 * Cue line immediately above an unheaded numbered procedure
 * (e.g. "Here are the steps for replacing…", "Follow the steps below:").
 */
function looksLikeStepsCueLine(line: string): boolean {
  const raw = String(line ?? "").trim();
  if (!raw || /^[-*•]\s+/.test(raw) || /^\d+[.)]\s+/.test(raw)) return false;
  if (isNumberedDocumentSectionHeading(raw)) return false;
  const t = stripInlineMarkdown(raw).replace(/:+$/, "").trim();
  if (!t || t.length > 140) return false;
  if (looksLikeImperativeProcedureLine(t)) return false;
  return /\b(steps?|procedure|instructions?|actions?)\b/i.test(t);
}

/**
 * Peel a trailing run of actionable numbered steps out of intro prose so they
 * become a Step Card section instead of Markdown. Used when the synthesizer
 * omits a structural heading before the main procedure but later includes one
 * (e.g. Post-Replacement Verification).
 */
function peelOrphanNumberedStepsFromIntro(intro: string): {
  intro: string;
  section: RawProcedureSection | null;
} {
  const raw = String(intro ?? "").replace(/\r\n/g, "\n");
  if (!raw.trim()) return { intro: "", section: null };

  const lines = raw.split("\n");
  let firstStepIdx = -1;

  for (let i = 0; i < lines.length; i++) {
    if (!isActionableNumberedStepLine(lines[i]!)) continue;
    const numberedFromHere = lines
      .slice(i)
      .filter((l) => isActionableNumberedStepLine(l)).length;
    if (numberedFromHere < 2) continue;

    // Trailing run only: every non-empty line after i must be a step or
    // continuation (bullet / prose), not a new structural heading.
    let valid = true;
    let sawStep = false;
    for (let j = i; j < lines.length; j++) {
      const t = lines[j]!.trim();
      if (!t) continue;
      if (isActionableNumberedStepLine(t)) {
        sawStep = true;
        continue;
      }
      if (!sawStep) {
        valid = false;
        break;
      }
      if (isWarningsNotesHeading(t) || tryStartWiSection(lines[j]!)) {
        valid = false;
        break;
      }
      // Continuations: bullets or plain prose folded under the prior step.
    }
    if (valid && sawStep) {
      firstStepIdx = i;
      break;
    }
  }

  if (firstStepIdx < 0) return { intro: raw.trim(), section: null };

  let preamble = lines.slice(0, firstStepIdx);
  const stepBody = lines.slice(firstStepIdx).join("\n").trim();
  if (!stepBody) return { intro: raw.trim(), section: null };

  let title = "Steps";
  while (preamble.length && !preamble[preamble.length - 1]!.trim()) {
    preamble = preamble.slice(0, -1);
  }
  if (preamble.length) {
    const last = preamble[preamble.length - 1]!.trim();
    if (looksLikeStepsCueLine(last)) {
      title = stripInlineMarkdown(last).replace(/:+$/, "").trim() || title;
      preamble = preamble.slice(0, -1);
    }
  }

  return {
    intro: preamble.join("\n").trim(),
    section: {
      title,
      body: stepBody,
      sectionNum: null,
      headingKind: "wi",
      synthetic: true,
    },
  };
}

/**
 * Extract WI-style sections from arbitrary text (answer or alert blob).
 * Does not require consecutive numbering from 1 — finds each structural
 * heading (e.g. `3. Tools & Materials Required`) and its body until the next.
 *
 * Also peels a trailing unheaded actionable numbered list out of the intro
 * into a synthetic steps section (orphan procedure before a later headed phase).
 */
export function extractRawProcedureSections(text: string): {
  intro: string;
  sections: RawProcedureSection[];
} {
  const normalized = String(text ?? "").replace(/\r\n/g, "\n");
  if (!normalized.trim()) return { intro: "", sections: [] };

  const lines = normalized.split("\n");
  const introLines: string[] = [];
  const sections: RawProcedureSection[] = [];
  let current: {
    title: string;
    bodyLines: string[];
    sectionNum: number | null;
    headingKind: "atx" | "wi";
    fromBareStep: boolean;
  } | null = null;

  const pushCurrent = () => {
    if (!current) return;
    sections.push({
      title: current.title,
      body: current.bodyLines.join("\n").trim(),
      sectionNum: current.sectionNum,
      headingKind: current.headingKind,
    });
    current = null;
  };

  for (const line of lines) {
    const trimmed = line.trim();

    const started = tryStartWiSection(line);
    if (started) {
      const bareStep = isBareStepPhaseHeading(line);
      // Nested `Step N:` under a numbered WI section (Health Check Steps) stays
      // in that section's body. Sibling `Step N:` phases under an ATX group
      // (or after another bare Step heading) still start new sections.
      if (current?.headingKind === "wi" && !current.fromBareStep && bareStep) {
        current.bodyLines.push(line);
        continue;
      }
      pushCurrent();
      current = {
        title: started.title,
        bodyLines: started.inlineBody ? [started.inlineBody] : [],
        sectionNum: started.sectionNum,
        headingKind: started.headingKind,
        fromBareStep: bareStep,
      };
      continue;
    }

    if (current) {
      current.bodyLines.push(line);
    } else if (trimmed) {
      if (isVideoSourceProvenanceNotice(trimmed)) continue;
      introLines.push(line);
    }
  }
  pushCurrent();

  const peeled = peelOrphanNumberedStepsFromIntro(introLines.join("\n").trim());
  if (peeled.section) {
    // Orphan steps appear before later headed sections in source order.
    sections.unshift(peeled.section);
  }

  return {
    intro: peeled.intro,
    sections,
  };
}

/**
 * Pull WI sections out of all pageAlerts entries (every page, every alert text).
 *
 * Alerts are often split into one heading entry + many follow-on bullet/prose
 * entries, or delivered as one aggregated string with mid-line headings and
 * `•` bullets. Normalize each blob, then parse with the line-based WI extractor.
 */
export function extractProcedureSectionsFromAlerts(
  pageAlerts:
    | Record<string, Array<{ text?: string; level?: string } | string>>
    | null
    | undefined
): RawProcedureSection[] {
  if (!pageAlerts) return [];

  const pages = Object.keys(pageAlerts).sort((a, b) => {
    const na = Number.parseInt(a, 10);
    const nb = Number.parseInt(b, 10);
    if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
    return a.localeCompare(b);
  });

  const chunks: string[] = [];
  for (const page of pages) {
    for (const entry of pageAlerts[page] ?? []) {
      const text =
        typeof entry === "string"
          ? entry
          : String(entry?.text ?? "").trim();
      if (text) chunks.push(text);
    }
  }
  if (!chunks.length) return [];

  const byKey = new Map<string, RawProcedureSection>();

  const consider = (sections: RawProcedureSection[]) => {
    for (const sec of sections) {
      const key = procedureSectionKey(sec.title);
      if (key === "warnings-notes") continue;
      const prev = byKey.get(key);
      // Prefer the richer body when the same section appears more than once.
      if (!prev || sec.body.length > prev.body.length) {
        byKey.set(key, sec);
      }
    }
  };

  // Primary: stitch + normalize aggregated mid-string headings / same-line bullets.
  consider(
    extractRawProcedureSections(
      normalizeAggregatedWiAlertText(chunks.join("\n"))
    ).sections
  );

  // Secondary: each alert may already be a self-contained multi-section blob.
  for (const chunk of chunks) {
    consider(
      extractRawProcedureSections(normalizeAggregatedWiAlertText(chunk)).sections
    );
  }

  return Array.from(byKey.values());
}

function insertSectionByNumber(
  merged: RawProcedureSection[],
  sec: RawProcedureSection
): void {
  if (sec.sectionNum != null) {
    let insertAt = merged.length;
    let foundNumberedAnchor = false;
    for (let i = 0; i < merged.length; i++) {
      const n = merged[i]!.sectionNum;
      if (n == null) continue;
      foundNumberedAnchor = true;
      if (n > sec.sectionNum) {
        insertAt = i;
        break;
      }
    }
    if (foundNumberedAnchor) {
      merged.splice(insertAt, 0, sec);
      return;
    }
    // Fall through: answer sections lack numbers — use family rank below.
  }

  // Soft-insert alert-only sections (Tools/Safety/…) without reordering
  // existing answer sections relative to each other.
  const rank = sectionSortRank(sec.title);
  let insertAt = merged.length;
  for (let i = 0; i < merged.length; i++) {
    if (sectionSortRank(merged[i]!.title) > rank) {
      insertAt = i;
      break;
    }
  }
  merged.splice(insertAt, 0, sec);
}

/**
 * Soft family rank for inserting alert-only sections among answer sections.
 * Never used to re-sort the answer's own section order.
 */
function sectionSortRank(title: string): number {
  const key = procedureSectionKey(title);
  if (key === "purpose") return 10;
  if (key === "before-you-begin" || key === "prerequisites") return 20;
  if (key === "tools") return 30;
  if (key === "safety") return 40;
  if (key.startsWith("steps:")) {
    if (/\bverif|post-install|post-replacement/.test(key)) return 120;
    if (
      key === "steps:replacement" ||
      key === "steps:removal" ||
      key === "steps:installation" ||
      key === "steps:main"
    ) {
      return 100;
    }
    return 110;
  }
  if (key === "documents") return 200;
  return 150;
}

/**
 * Answer sections win and keep their source order. Alert-only sections are
 * inserted by section number (when available) or soft family rank, without
 * duplicating an answer section of the same family and without re-sorting
 * answer sections by title.
 */
export function mergeProcedureSections(
  answerSections: RawProcedureSection[],
  alertSections: RawProcedureSection[]
): RawProcedureSection[] {
  const merged = [...answerSections];
  const keys = new Set(answerSections.map((s) => procedureSectionKey(s.title)));

  for (const sec of alertSections) {
    const key = procedureSectionKey(sec.title);
    if (key === "warnings-notes") continue;
    if (keys.has(key)) continue;
    keys.add(key);
    insertSectionByNumber(merged, sec);
  }

  return merged;
}

function classifyRawSections(sections: RawProcedureSection[]): ProcedureBlock[] {
  const out: ProcedureBlock[] = [];
  let currentGroupTitle: string | null = null;
  let groupHasChildren = false;
  let nestedTitle: string | null = null;

  for (const sec of sections) {
    let { prose, items } = extractBodyItems(sec.body);
    // Split pageAlerts often omit "- " markers; treat each prose line as a list item.
    if (
      items.length === 0 &&
      prose &&
      (isToolsSectionTitle(sec.title) || isSafetySectionTitle(sec.title))
    ) {
      const lines = prose
        .split(/\n+/)
        .map((l) => l.replace(/^[-*•]\s+/, "").trim())
        .filter(Boolean);
      if (lines.length > 0) {
        items = lines.map((text) => ({ num: null, text }));
        prose = "";
      }
    }
    const displayTitle =
      stripLeadingWiSectionNumber(normalizeWiTitle(sec.title)).title ||
      normalizeWiTitle(sec.title) ||
      sec.title;
    const hasContent = items.length > 0 || Boolean(prose?.trim());
    const isAtx = sec.headingKind === "atx";

    if (isAtx) {
      const group = displayTitle.trim();
      if (!hasContent) {
        if (currentGroupTitle && !groupHasChildren) {
          // Consecutive empty ATX: keep the outer group; a distinct title is nested.
          if (
            group &&
            group.toLowerCase() !== currentGroupTitle.toLowerCase()
          ) {
            nestedTitle = group;
          }
          continue;
        }
        currentGroupTitle = group || currentGroupTitle;
        nestedTitle = null;
        groupHasChildren = false;
        continue;
      }
      currentGroupTitle = group || currentGroupTitle;
      nestedTitle = null;
      groupHasChildren = true;
    }

    const classified = classifyProcedureBlock(displayTitle, prose, items);
    out.push({
      title: displayTitle,
      kind: classified.kind,
      compactVariant: classified.compactVariant,
      body: classified.body,
      items: classified.items,
      groupTitle: currentGroupTitle,
      nestedTitle,
    });
    groupHasChildren = true;
  }
  return out;
}

/** Drop empty umbrella headings (e.g. bare "Battery Health Check Procedure:"). */
function isEmptyPlaceholderBody(text: string): boolean {
  return /^no information available(?:\s+for this query)?\.?$/i.test(
    String(text ?? "").trim()
  );
}

function hasRenderableProcedureContent(block: ProcedureBlock): boolean {
  const itemText = block.items.map((it) => it.text).join("\n").trim();
  const body = (block.body || "").trim();
  if (!itemText && !body) return false;
  if (!itemText && isEmptyPlaceholderBody(body)) return false;
  if (!body && block.items.length > 0 && block.items.every((it) => isEmptyPlaceholderBody(it.text))) {
    return false;
  }
  return true;
}

/**
 * Lift a leading empty non-ATX heading (document title) off the section list.
 * ATX headings are group titles and may appear more than once — do not peel.
 */
function peelEmptyLeadingUmbrella(sections: RawProcedureSection[]): {
  documentTitle: string | null;
  sections: RawProcedureSection[];
} {
  if (sections.length < 2) return { documentTitle: null, sections };
  const first = sections[0]!;
  if (first.headingKind === "atx") return { documentTitle: null, sections };
  const displayTitle =
    stripLeadingWiSectionNumber(normalizeWiTitle(first.title)).title ||
    normalizeWiTitle(first.title) ||
    first.title;
  if (
    isToolsSectionTitle(displayTitle) ||
    isSafetySectionTitle(displayTitle) ||
    isStandaloneCautionSectionTitle(displayTitle) ||
    isDocumentsSectionTitle(displayTitle) ||
    isProseLeadSectionTitle(displayTitle)
  ) {
    return { documentTitle: null, sections };
  }
  const { prose, items } = extractBodyItems(first.body);
  if (prose || items.length) return { documentTitle: null, sections };
  if (!displayTitle.trim()) return { documentTitle: null, sections };
  return { documentTitle: displayTitle.trim(), sections: sections.slice(1) };
}

function hasHeadedProcedureSections(sections: RawProcedureSection[]): boolean {
  if (sections.length >= 2) return true;
  if (sections.length !== 1) return false;
  const section = sections[0]!;
  if (section.synthetic) return false;
  if (section.headingKind === "atx") return true;
  return Boolean(section.title && isStructuralWiHeading(section.title));
}

function toParsedProcedureDocument(
  intro: string,
  sections: RawProcedureSection[]
): ParsedProcedureDocument | null {
  if (!hasHeadedProcedureSections(sections)) return null;
  const { documentTitle, sections: rest } = peelEmptyLeadingUmbrella(sections);
  const blocks = classifyRawSections(rest).filter(hasRenderableProcedureContent);
  if (!blocks.some((b) => b.kind === "steps")) return null;
  return {
    intro: intro || null,
    documentTitle,
    blocks,
  };
}

/**
 * Compact sections present in pageAlerts but missing from the answer.
 * Used when the WI document parser returns null and the UI falls back to
 * legacy Step Card carousels — still surface Tools (not Safety) from alerts.
 */
export function getAlertFallbackCompactBlocks(
  answer: string,
  pageAlerts?:
    | Record<string, Array<{ text?: string; level?: string } | string>>
    | null,
  existingSectionTitles: Array<string | null | undefined> = []
): ProcedureBlock[] {
  const fromAlerts = extractProcedureSectionsFromAlerts(pageAlerts);
  if (!fromAlerts.length) return [];

  const fromAnswer = extractRawProcedureSections(answer);
  const keys = new Set([
    ...fromAnswer.sections.map((s) => procedureSectionKey(s.title)),
    ...existingSectionTitles
      .filter((t): t is string => Boolean(t && String(t).trim()))
      .map((t) => procedureSectionKey(t)),
  ]);

  return classifyRawSections(
    fromAlerts.filter((s) => !keys.has(procedureSectionKey(s.title)))
  )
    .filter((b) => b.kind === "compact")
    // Safety Precautions feed the top Safety notices rail — never an inline compact.
    // Standalone "Do Not" / caution blocks may still render inline.
    .filter(
      (b) =>
        b.compactVariant !== "safety" ||
        isStandaloneCautionSectionTitle(b.title)
    );
}

/**
 * Pull Safety Precautions bullet/prose items from a WI answer for the existing
 * top-level Safety notices collection. Does not read pageAlerts (those alerts
 * are already in that collection).
 */
export function extractSafetyPrecautionsAlerts(answer: string): Array<{
  level: string;
  text: string;
}> {
  const { sections } = extractRawProcedureSections(answer);
  const safetySections = sections.filter(
    (s) => procedureSectionKey(s.title) === "safety"
  );
  if (!safetySections.length) return [];

  const alerts: Array<{ level: string; text: string }> = [];
  const seen = new Set<string>();

  for (const block of classifyRawSections(safetySections)) {
    const texts =
      block.items.length > 0
        ? block.items.map((i) => i.text)
        : block.body
          ? block.body
              .split(/\n+/)
              .map((l) => l.replace(/^[-*•]\s+/, "").trim())
              .filter(Boolean)
          : [];

    for (const raw of texts) {
      const text = String(raw ?? "")
        .replace(/^[-*•]\s+/, "")
        .replace(/\s+/g, " ")
        .trim();
      if (!text || text.length < 4) continue;
      if (isSafetySectionTitle(text) || isToolsSectionTitle(text)) continue;
      const key = text.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      alerts.push({ level: "warning", text });
    }
  }

  return alerts;
}

/**
 * Procedure body UI: keep Tools / Purpose / Steps / etc., but never render
 * Safety Precautions inline (those belong in the top Safety notices rail).
 * Standalone caution blocks ("Do Not") stay inline below the steps.
 */
export function procedureBlocksForDisplay(
  blocks: ProcedureBlock[]
): ProcedureBlock[] {
  return blocks.filter((b) => {
    // Same as feat-deploy-RAG-integration: Warnings and Notes are not a
    // procedure canvas section (Safety notices rail reads them from the answer).
    if (isWarningsNotesHeading(b.title) || procedureSectionKey(b.title) === "warnings-notes") {
      return false;
    }
    if (b.compactVariant !== "safety") return true;
    return isStandaloneCautionSectionTitle(b.title);
  });
}

/**
 * Split display blocks so Tools can render above the intro (immediately under
 * the top Safety notices rail), with remaining sections after the intro.
 */
export function splitProcedureBlocksForDisplay(blocks: ProcedureBlock[]): {
  toolsBlocks: ProcedureBlock[];
  bodyBlocks: ProcedureBlock[];
} {
  const display = procedureBlocksForDisplay(blocks);
  const hasGroups = display.some((b) => Boolean(b.groupTitle));
  // Multi-group ATX answers keep Tools under their group, not hoisted above titles.
  if (hasGroups) {
    return { toolsBlocks: [], bodyBlocks: display };
  }
  return {
    toolsBlocks: display.filter((b) => b.compactVariant === "tools"),
    bodyBlocks: display.filter((b) => b.compactVariant !== "tools"),
  };
}

/**
 * Conceptual render order for the procedure answer surface (Safety notices rail
 * lives outside RagAnswerText and is always first when present).
 */
export function procedureAnswerRenderOrder(options: {
  hasSafetyNotices: boolean;
  blocks: ProcedureBlock[];
  hasIntro: boolean;
}): string[] {
  const { toolsBlocks, bodyBlocks } = splitProcedureBlocksForDisplay(
    options.blocks
  );
  const order: string[] = [];
  if (options.hasSafetyNotices) order.push("safety-notices");
  if (toolsBlocks.length) order.push("tools");
  if (options.hasIntro) order.push("intro");
  for (const block of bodyBlocks) {
    if (block.kind === "steps") {
      order.push(
        /\bverif/i.test(block.title) ? "steps:verification" : "steps"
      );
    } else {
      order.push(block.compactVariant ?? "compact");
    }
  }
  return order;
}

/**
 * Parse work-instruction style answers into titled sections, then classify each
 * as a compact Section Card or a Step Card carousel.
 *
 * Returns null when the answer is not a multi-section WI document (keeps the
 * existing flat Step Card path for simple numbered procedures).
 */
export function parseProcedureDocument(text: string): ParsedProcedureDocument | null {
  const { intro, sections } = extractRawProcedureSections(text);
  return toParsedProcedureDocument(intro, sections);
}

/**
 * Same as {@link parseProcedureDocument}, but fills missing *supporting*
 * sections from `pageAlerts` when the answer omits them (answer still wins
 * on duplicates). Alert-derived step carousels are never merged — Step Cards
 * come only from the answer.
 */
export function parseProcedureDocumentWithAlerts(
  answer: string,
  pageAlerts?:
    | Record<string, Array<{ text?: string; level?: string } | string>>
    | null
): ParsedProcedureDocument | null {
  const fromAnswer = extractRawProcedureSections(answer);
  const fromAlerts = extractProcedureSectionsFromAlerts(pageAlerts);
  // pageAlerts may contain full WI procedure phases (e.g. Replacement Steps).
  // Only compact supporting sections (Tools, Safety, Purpose, …) are eligible.
  const alertSupportSections = fromAlerts.filter((section) => {
    const blocks = classifyRawSections([section]);
    return blocks.length > 0 && blocks[0]!.kind === "compact";
  });
  const { documentTitle, sections: answerSections } = peelEmptyLeadingUmbrella(
    fromAnswer.sections
  );
  const mergedSections = mergeProcedureSections(
    answerSections,
    alertSupportSections
  );

  if (
    !hasHeadedProcedureSections(fromAnswer.sections) &&
    !hasHeadedProcedureSections(mergedSections)
  ) {
    return null;
  }

  const blocks = classifyRawSections(mergedSections).filter(
    hasRenderableProcedureContent
  );
  if (!blocks.some((b) => b.kind === "steps")) return null;

  return {
    intro: fromAnswer.intro || null,
    documentTitle,
    blocks,
  };
}

const PROCEDURAL_VERB_RE =
  /\b(remove|install|attach|disconnect|connect|loosen|tighten|bleed|check|inspect|measure|replace|apply|coat|lubricate|secure|cover|drive|use|open|close|press|turn|lift|lower|align|insert|pull|push|clean|wipe|drain|refill|confirm|verify|ensure|locate|observe|repeat|move|submerge|flush)\b/i;

/** Common engineering units for generic measurement/spec detection. */
const MEASUREMENT_UNIT =
  "(?:mm|cm|dm|m|in(?:ch(?:es)?)?|ft|(?:n[·\\s]?m|nm)|bar|psi|kpa|mpa|pa|kg|g|lb|lbs|l|ml|gal|rpm|°|deg|%)";

const PURE_MEASUREMENT_RE = new RegExp(
  `^\\d+(?:\\.\\d+)?(?:\\s*(?:[-–]\\s*\\d+(?:\\.\\d+)?))?\\s*${MEASUREMENT_UNIT}\\b`,
  "i"
);

const TO_MEASUREMENT_RE = new RegExp(
  `\\bto\\s+\\d+(?:\\.\\d+)?\\s*${MEASUREMENT_UNIT}\\b`,
  "i"
);

const TRAILING_MEASUREMENT_RE = new RegExp(
  `\\d+(?:\\.\\d+)?\\s*${MEASUREMENT_UNIT}\\.?\\s*$`,
  "i"
);

/** Limit keyword with a nearby numeric value (not "MIN/MAX markings" alone). */
const LIMIT_WITH_NUMBER_RE =
  /\b(?:minimum|maximum|min\.?|max\.?)\s*[:=]?\s*\d/i;
const TOLERANCE_SPEC_RE = /\b(?:tolerance|clearance|limit)\b/i;

/** True when a line primarily states a measurement, limit, or spec value. */
export function looksLikeSpecificationLine(text: string): boolean {
  const clean = cleanStepDisplayText(stripInlineMarkdown(text)).trim();
  if (!clean) return false;

  if (PURE_MEASUREMENT_RE.test(clean)) return true;
  if (TO_MEASUREMENT_RE.test(clean)) return true;
  if (TRAILING_MEASUREMENT_RE.test(clean)) return true;
  // Require a number next to min/max — "MIN/MAX markings" in a procedure
  // step must not poison classification (packed lines often also contain "2.").
  if (LIMIT_WITH_NUMBER_RE.test(clean)) return true;
  if (TOLERANCE_SPEC_RE.test(clean) && /\d/.test(clean)) return true;

  return false;
}

/**
 * True when a line reads as an imperative procedure instruction
 * (e.g. "Remove the cover", "Disconnect the cable"), not a part name
 * that happens to contain a verb-like token ("Dust cover", "Push rod").
 */
function looksLikeImperativeProcedureLine(text: string): boolean {
  const clean = cleanStepDisplayText(stripInlineMarkdown(text)).trim();
  if (!clean) return false;
  // Phrasal service actions that are not single lexicon verbs.
  if (/^top\s+off\b/i.test(clean) || /\btop\s+off\b/i.test(clean)) {
    return true;
  }
  if (/^(?:then|next|now)[,:]?\s+/i.test(clean)) {
    return PROCEDURAL_VERB_RE.test(clean.replace(/^(?:then|next|now)[,:]?\s+/i, ""));
  }
  // Leading imperative verb: "Remove the …", "Checking …"
  if (new RegExp(`^${PROCEDURAL_VERB_RE.source}`, "i").test(clean)) {
    return true;
  }
  // Common imperative clause shape: "<verb> the|a|an|all …"
  if (
    PROCEDURAL_VERB_RE.test(clean) &&
    /\b(?:the|a|an|all|any|both|each)\b/i.test(clean)
  ) {
    return true;
  }
  // Participial openers: "Starting with…", "Having a helper…"
  if (/^(?:starting|having|using|keeping|making)\b/i.test(clean)) {
    return true;
  }
  return false;
}

/**
 * True when a short title looks like a document / WI section heading
 * (Purpose, Product Categories, General Guidance, Related Documents, …)
 * rather than an imperative instruction.
 */
export function isDocumentSectionLeadingTitle(title: string): boolean {
  const t = normalizeWiTitle(title);
  if (!t || t.length > 80) return false;
  if (looksLikeImperativeProcedureLine(t)) return false;
  if (/[.!?]$/.test(t)) return false;

  // Use known section families + phase nouns — not the loose titleish
  // fallback in isWorkInstructionSectionTitle (which matches "Locate", "Bolt",
  // "Top", … and falsely turns action steps into document sections).
  if (
    isProseLeadSectionTitle(t) ||
    isDocumentsSectionTitle(t) ||
    isToolsSectionTitle(t) ||
    isSafetySectionTitle(t) ||
    isStandaloneCautionSectionTitle(t) ||
    isWarningsNotesHeading(t) ||
    isStepsPhaseSectionTitle(t)
  ) {
    return true;
  }

  // Reference-style headings that may not hit WI families
  // (e.g. "Product Categories", "General Guidance", "Notes").
  if (/^(?:notes?|overview|categories|guidance|guidelines|recommendations)\b/i.test(t)) {
    return true;
  }
  if (
    /\b(categories|overview|guidance|guidelines|recommendations|information)\b/i.test(t) &&
    t.split(/\s+/).length <= 6
  ) {
    return true;
  }

  return false;
}

/**
 * True when a Markdown line is a numbered document-section heading
 * (e.g. `1. Purpose`, `2. **Product Categories**`), not an imperative step
 * (`1. Remove the battery cover`).
 */
export function isNumberedDocumentSectionHeading(line: string): boolean {
  const trimmed = String(line ?? "").trim();
  const m = trimmed.match(/^(\d+)[.)]\s+(.+)$/);
  if (!m) return false;
  const rest = stripInlineMarkdown(m[2] ?? "").trim();
  if (!rest) return false;
  if (looksLikeImperativeProcedureLine(rest)) return false;
  return isDocumentSectionLeadingTitle(rest);
}

/**
 * Leading title span of a folded numbered item (before body prose / bullets).
 * Returns null when no section-like title prefix is found.
 */
function extractNumberedItemLeadingTitle(text: string): string | null {
  const clean = cleanStepDisplayText(stripInlineMarkdown(text)).trim();
  if (!clean) return null;

  // "Purpose: Explain the workflow…" / "Preparation: Remove the cover…"
  const colonIdx = clean.indexOf(":");
  if (colonIdx > 0 && colonIdx <= 60) {
    const name = clean.slice(0, colonIdx).trim();
    if (name && name.split(/\s+/).length <= 8 && !looksLikeImperativeProcedureLine(name)) {
      return name;
    }
  }

  // Cut before folded bullet markers and "Title - body" dash separators
  // introduced by parseNumberedSteps ("Related Documents - WI-…").
  const head = clean.split(/\s*(?:[•]|[-–—])\s+/)[0]!.trim();
  if (!head) return null;

  const tokens = head.split(/\s+/).filter(Boolean);
  // Prefer the shortest valid section title so body words after a dash/colon
  // never inflate "Related Documents Condition…" into a fake heading.
  for (let n = 1; n <= Math.min(8, tokens.length); n++) {
    const candidate = tokens.slice(0, n).join(" ");
    if (!isDocumentSectionLeadingTitle(candidate)) continue;
    return candidate;
  }
  return null;
}

/**
 * Numbered item is a reference/WI document section (heading + prose/bullets),
 * not an actionable procedure step. Body verbs must not make it procedural.
 */
function isNumberedDocumentSectionItem(text: string): boolean {
  const clean = cleanStepDisplayText(stripInlineMarkdown(text)).trim();
  if (!clean) return false;

  // "Title: Remove the cover…" is an actionable titled step, not a section card.
  const colonIdx = clean.indexOf(":");
  if (colonIdx > 0 && colonIdx <= 60) {
    const name = clean.slice(0, colonIdx).trim();
    const description = clean.slice(colonIdx + 1).trim();
    if (
      description &&
      looksLikeImperativeProcedureLine(description) &&
      // Keep "Purpose: Explain…" as a section; only reject when the title is
      // a short phase label with an immediate imperative body.
      !isProseLeadSectionTitle(name) &&
      !isDocumentsSectionTitle(name) &&
      !isToolsSectionTitle(name) &&
      !isSafetySectionTitle(name)
    ) {
      return false;
    }
  }

  const leading = extractNumberedItemLeadingTitle(clean);
  if (!leading || !isDocumentSectionLeadingTitle(leading)) return false;
  if (looksLikeImperativeProcedureLine(leading)) return false;

  // Only the text immediately after the title decides "section vs instruction".
  // Do not scan the whole folded body for verbs (use/apply/check in bullets).
  const leadAt = clean.toLowerCase().indexOf(leading.toLowerCase());
  const rest =
    leadAt >= 0
      ? clean.slice(leadAt + leading.length).replace(/^[\s:—–-]+/, "").trim()
      : "";
  if (!rest) return true;

  // "Do not assume…" / "Never ignore…" prose instructions are steps, not a
  // bare caution section heading with bullet body.
  if (isStandaloneCautionSectionTitle(leading)) {
    if (/^[-*•]/.test(rest)) return true;
    return false;
  }

  // Phase nouns mid-instruction ("… a service location", "Once stopped, check")
  // must not collapse a full actionable line into a document section.
  // Only apply when the leading title matched via steps-phase nouns — never
  // strip Purpose / Categories / Guidance / Related Documents reference heads.
  const leadNorm = normalizeWiTitle(leading);
  const referenceHeading =
    isProseLeadSectionTitle(leadNorm) ||
    isDocumentsSectionTitle(leadNorm) ||
    isToolsSectionTitle(leadNorm) ||
    isSafetySectionTitle(leadNorm) ||
    isWarningsNotesHeading(leadNorm) ||
    /^(?:notes?|overview|categories|guidance|guidelines|recommendations)\b/i.test(
      leadNorm
    ) ||
    (/\b(categories|overview|guidance|guidelines|recommendations|information)\b/i.test(
      leadNorm
    ) &&
      leadNorm.split(/\s+/).length <= 6);
  const leadWords = leading.split(/\s+/).filter(Boolean).length;
  const cleanPhaseHeading =
    leadWords <= 4 &&
    !/,/.test(leading) &&
    /\b(steps?|procedure|verification|inspection|checks?|tests?|guide|guidance|phase|actions?|assessment|evaluation)\s*$/i.test(
      leading
    );
  if (
    !referenceHeading &&
    isStepsPhaseSectionTitle(leading) &&
    !cleanPhaseHeading &&
    looksLikeImperativeProcedureLine(clean)
  ) {
    return false;
  }

  const restLead = rest.split(/(?<=[.!?])\s+/)[0]!.trim().slice(0, 120);
  // Immediate imperative after the heading → actionable step ("Steps Remove…").
  if (new RegExp(`^${PROCEDURAL_VERB_RE.source}`, "i").test(restLead)) {
    return false;
  }
  return true;
}

function looksLikeProceduralAction(text: string): boolean {
  const clean = cleanStepDisplayText(stripInlineMarkdown(text));
  if (!clean) return false;
  if (looksLikeSpecificationLine(text)) return false;

  // Reference / WI section headings with nested prose or bullets are not steps,
  // even when body text contains verbs like "use", "apply", "check", "replace".
  if (isNumberedDocumentSectionItem(text)) return false;

  // "Title: Loosen the hold-down…" — body is the procedural action.
  const colonIdx = clean.indexOf(":");
  if (colonIdx > 0 && colonIdx <= 60) {
    const description = clean.slice(colonIdx + 1).trim();
    if (description && looksLikeImperativeProcedureLine(description)) {
      return true;
    }
  }

  // Catalog-style legend lines are never procedural steps.
  if (looksLikeCatalogLabel(text)) return false;
  return looksLikeImperativeProcedureLine(clean) || PROCEDURAL_VERB_RE.test(clean);
}

/**
 * Diagram/component callout labels (and Name: note legends).
 * Verbs in the optional note ("Always replace") must NOT poison classification.
 * Part names that overlap the verb lexicon ("Cover", "Push rod") stay catalog.
 * Video/procedure step titles with imperative bodies are NOT catalog labels.
 */
function looksLikeCatalogLabel(text: string): boolean {
  const clean = cleanStepDisplayText(stripInlineMarkdown(text)).trim();
  if (!clean) return false;
  if (looksLikeSpecificationLine(text)) return false;

  const colonIdx = clean.indexOf(":");
  if (colonIdx > 0 && colonIdx <= 60) {
    const name = clean.slice(0, colonIdx).trim();
    const description = clean.slice(colonIdx + 1).trim();
    if (!name || name.split(/\s+/).length > 8) return false;
    if (looksLikeImperativeProcedureLine(name)) return false;
    // Imperative procedure body after a short title → procedure step, not legend.
    if (description && looksLikeImperativeProcedureLine(description)) {
      return false;
    }
    return true;
  }

  // Short noun-phrase callouts without sentence punctuation.
  if (clean.length <= 60 && !/[.!?]/.test(clean)) {
    if (looksLikeImperativeProcedureLine(clean)) return false;
    return clean.split(/\s+/).length <= 8;
  }

  return false;
}

export function classifyNumberedAnswerKind(
  steps: ParsedStep[]
): "procedure" | "spec-list" {
  const numbered = steps.filter((s) => s.num);
  if (!numbered.length) return "procedure";

  const specCount = numbered.filter((s) => looksLikeSpecificationLine(s.text)).length;
  const sectionCount = numbered.filter((s) =>
    isNumberedDocumentSectionItem(s.text)
  ).length;
  // Document-section headings (Decision Guide, Related Documents, …) often match
  // looksLikeCatalogLabel as short noun phrases — do not count them as catalog.
  const catalogCount = numbered.filter(
    (s) =>
      !isNumberedDocumentSectionItem(s.text) && looksLikeCatalogLabel(s.text)
  ).length;
  const proceduralCount = numbered.filter((s) => looksLikeProceduralAction(s.text)).length;
  const dashCount = numbered.filter((s) => s.marker === "dash").length;

  // A single numbered line is never a multi-step procedure workflow.
  if (numbered.length === 1) {
    return "spec-list";
  }

  // Reference documents whose numbered items are only section headings
  // (Purpose / Categories / Guidance / Related Documents) are not procedures.
  if (numbered.length >= 2 && sectionCount === numbered.length) {
    return "spec-list";
  }
  if (numbered.length >= 2 && sectionCount >= 2 && proceduralCount < 2) {
    return "spec-list";
  }

  if (specCount / numbered.length >= 0.5) {
    return "spec-list";
  }

  if (catalogCount / numbered.length >= 0.5) {
    return "spec-list";
  }

  if (numbered.length >= 5 && catalogCount / numbered.length >= 0.6) {
    return "spec-list";
  }

  // Dense "1 - Label" diagram legends — prefer catalog/spec over Step Cards
  // even when a few notes contain service verbs.
  if (
    numbered.length >= 3 &&
    dashCount / numbered.length >= 0.7 &&
    catalogCount > 0 &&
    catalogCount >= proceduralCount
  ) {
    return "spec-list";
  }

  // Genuine workflow: multiple steps dominated by sequential actions.
  // Require procedural to *strictly* outnumber catalog (ties → not procedure).
  if (
    numbered.length >= 2 &&
    proceduralCount >= 2 &&
    proceduralCount >= specCount &&
    proceduralCount > catalogCount
  ) {
    return "procedure";
  }

  if (
    numbered.length >= 2 &&
    proceduralCount / numbered.length >= 0.5 &&
    specCount === 0 &&
    catalogCount === 0
  ) {
    return "procedure";
  }

  return "spec-list";
}

/** True when the answer uses the procedure Step Card layout (not catalog/spec-list). */
export function isProcedureAnswer(text: string): boolean {
  const steps = parseNumberedSteps(text);
  if (!steps?.some((s) => s.num)) return false;
  if (classifyNumberedAnswerKind(steps) === "procedure") return true;
  // Backend video_primary path: notice + numbered steps → Step Cards.
  return isVideoPrimaryProcedureFallback(steps);
}

/**
 * Whether RagAnswerText / mobile should render the Step Card carousel.
 * Parts catalog wins; otherwise procedure classification or video-primary fallback.
 */
export function shouldUseProcedureStepCards(
  steps: ParsedStep[] | null | undefined,
  numberedKind: "procedure" | "spec-list" | null,
  usePartsCatalog: boolean
): boolean {
  if (!steps?.some((s) => s.num) || usePartsCatalog) return false;
  if (numberedKind === "procedure") return true;
  return isVideoPrimaryProcedureFallback(steps);
}

export interface QueuedInlineImage {
  img: RagImageItem;
  page: string;
}

/** Page-ordered queue for [img:N] tags and proportional fallback. */
export function buildOrderedImageQueue(
  inlineImages: Record<string, RagImageItem[]>
): QueuedInlineImage[] {
  const sorted = Object.keys(inlineImages)
    .filter((p) => /^\d+$/.test(p))
    .sort((a, b) => Number(a) - Number(b));
  const queue: QueuedInlineImage[] = [];
  for (const page of sorted) {
    for (const img of inlineImages[page] ?? []) {
      queue.push({ img, page });
    }
  }
  return queue;
}

/** Strip [p.N] and [img:N] markers from step text for display. */
export function cleanStepDisplayText(text: string): string {
  return text.replace(/\[p\.\d+\]/g, "").replace(/\[img:\d+\]\s*/g, "").trim();
}

/** Remove common inline markdown markers for plain-text UI labels. */
export function stripInlineMarkdown(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/_([^_]+)_/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\*\*/g, "")
    .replace(/__/g, "")
    .trim();
}

function normalizeProcedureHeadingKey(text: string): string {
  return stripInlineMarkdown(cleanStepDisplayText(text))
    .replace(/:+$/, "")
    .trim()
    .toLowerCase();
}

/**
 * Leading non-numbered prose before the first procedure step, for display above
 * the Step Card carousel. Skips video provenance notices and lines already used
 * as procedure section titles (avoids duplicating the section badge).
 */
export function extractProcedureIntroProse(
  steps: ParsedStep[],
  sectionTitles: Array<string | null | undefined> = []
): string | null {
  const titleKeys = new Set(
    sectionTitles
      .filter((t): t is string => Boolean(t && String(t).trim()))
      .map((t) => normalizeProcedureHeadingKey(t))
      .filter(Boolean)
  );

  const introLines: string[] = [];
  for (const step of steps) {
    if (step.num) break;
    if (isVideoSourceProvenanceNotice(step.text)) continue;

    const display = stripInlineMarkdown(cleanStepDisplayText(step.text)).trim();
    if (!display) continue;
    if (titleKeys.has(normalizeProcedureHeadingKey(display))) continue;

    // Numbered WI headings (`1. Purpose`) must not leak into legacy intro.
    const { title: stripped } = stripLeadingWiSectionNumber(display);
    if (stripped && titleKeys.has(normalizeProcedureHeadingKey(stripped))) {
      continue;
    }
    if (stripped && isStructuralWiHeading(stripped)) {
      continue;
    }

    introLines.push(display);
  }

  const joined = introLines.join("\n\n").trim();
  return joined || null;
}

/** Split cleaned step prose into a card title and body for step-card UI. */
export function splitStepTitleBody(text: string): { title: string; body: string } {
  const clean = stripInlineMarkdown(cleanStepDisplayText(text));
  if (!clean) return { title: "", body: "" };

  // Prefer first line as title when continuation lines exist and the first has no colon
  // (e.g. "Before You Begin\nIMPORTANT: …" before parseNumberedSteps folds them).
  const lines = clean
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length >= 2 && !lines[0]!.includes(":")) {
    return { title: lines[0]!, body: lines.slice(1).join(" ") };
  }

  // Folded advisory labels: "Before You Begin IMPORTANT: Do not proceed…"
  const advisoryMatch = clean.match(
    /^(.{1,80}?)\s+((?:IMPORTANT|WARNING|CAUTION|NOTE|DANGER)\s*:\s+[\s\S]+)$/i
  );
  if (advisoryMatch) {
    return { title: advisoryMatch[1]!.trim(), body: advisoryMatch[2]!.trim() };
  }

  const colonMatch = clean.match(/^([^:]+):\s+([\s\S]+)$/);
  if (colonMatch && colonMatch[1]!.length <= 80) {
    return { title: colonMatch[1]!.trim(), body: colonMatch[2]!.trim() };
  }

  const periodMatch = clean.match(/^([^.!?]+[.!?])\s+([\s\S]+)$/);
  if (periodMatch && periodMatch[1].length <= 120) {
    return { title: periodMatch[1].trim(), body: periodMatch[2].trim() };
  }

  if (lines.length >= 2) {
    return { title: lines[0]!, body: lines.slice(1).join(" ") };
  }

  if (clean.length <= 60) {
    return { title: clean, body: "" };
  }

  return { title: "", body: clean };
}

/** Extract a trailing markdown link for optional step action footer. */
export function extractStepActionLink(text: string): {
  text: string;
  link?: { label: string; url: string };
} {
  const match = text.match(/\[([^\]]+)\]\(([^)]+)\)\s*$/);
  if (!match) return { text };
  return {
    text: text.replace(match[0], "").trim(),
    link: { label: match[1].trim(), url: match[2].trim() },
  };
}

/** Matches inline video citations such as "(Video 5)" in step descriptions. */
export const STEP_VIDEO_REF_PATTERN = /\(\s*Video\s+(\d+)\s*\)/gi;

/** Collect 1-based video numbers referenced in answer/step text. */
export function findReferencedVideoNumbers(text: string): number[] {
  const nums: number[] = [];
  const re = new RegExp(STEP_VIDEO_REF_PATTERN.source, "gi");
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const n = Number.parseInt(match[1], 10);
    if (Number.isFinite(n) && n > 0) nums.push(n);
  }
  return nums;
}

/** Remove "(Video N)" citations from display text while preserving order for bindings. */
export function stripStepVideoReferences(text: string): string {
  return String(text ?? "")
    .replace(new RegExp(STEP_VIDEO_REF_PATTERN.source, "gi"), " ")
    .replace(/[ \t]+([,.;:!?])/g, "$1")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/[ \t]+$/gm, "")
    .trim();
}

/**
 * Display-only: hide "(Video N)" when those clips are shown on the rail or
 * step cards. Does not mutate the stored answer string.
 */
export function stripDisplayedVideoCitations(
  text: string,
  videos?: RagVideoItem[] | null
): string {
  if (!videos?.length) return String(text ?? "");
  return stripStepVideoReferences(text);
}

/**
 * Resolve "(Video N)" citations to playback items (1-based index into `availableVideos`).
 * Original text is not mutated; returns cleaned display text and ordered bindings.
 */
export function extractStepVideoReferences(
  text: string,
  availableVideos: RagVideoItem[] = []
): { text: string; videos: StepVideoBinding[] } {
  const seen = new Set<number>();
  const videos: StepVideoBinding[] = [];

  for (const videoNum of findReferencedVideoNumbers(text)) {
    if (seen.has(videoNum)) continue;
    seen.add(videoNum);
    const item = availableVideos[videoNum - 1];
    if (item?.path) videos.push({ videoNum, item });
  }

  return { text: stripStepVideoReferences(text), videos };
}

/** Filename of a media URL, ignoring query strings and time fragments. */
export function videoFileKey(path: string): string {
  const noHash = String(path || "").split("#")[0] ?? "";
  const noQuery = noHash.split("?")[0] ?? "";
  const file = noQuery.replace(/\\/g, "/").split("/").pop() ?? "";
  try {
    return decodeURIComponent(file).toLowerCase();
  } catch {
    return file.toLowerCase();
  }
}

/** HTML Media Fragments (`#t=start,end`) on a URL that may already have `?token=`. */
export function withMediaTimeFragment(
  path: string,
  start?: number | null,
  end?: number | null
): string {
  if (!path || start == null || !Number.isFinite(Number(start))) return path;
  const base = path.split("#")[0] ?? path;
  const t0 = Number(start);
  const t1 = end == null ? null : Number(end);
  const spec =
    t1 != null && Number.isFinite(t1) ? `t=${t0},${t1}` : `t=${t0}`;
  return `${base}#${spec}`;
}

export function procedureVideoToItem(
  video: RagProcedureVideo | null | undefined
): RagVideoItem | null {
  if (!video?.path) return null;
  return {
    path: withMediaTimeFragment(video.path, video.start_time, video.end_time),
    name: videoDisplayName(video.path),
    startTime: video.start_time ?? null,
    endTime: video.end_time ?? null,
  };
}

/** Normalize a step's ``video`` field (list or legacy single object). */
export function procedureStepVideoList(
  video: RagProcedureStep["video"] | null | undefined
): RagProcedureVideo[] {
  if (!video) return [];
  const arr = Array.isArray(video) ? video : [video];
  return arr.filter((v) => Boolean(v?.path));
}

/** Action-step clips from the worker procedure payload, in document order. */
export function procedureStepVideoItems(
  procedure: RagProcedure | null | undefined
): RagVideoItem[] {
  const out: RagVideoItem[] = [];
  for (const step of procedure?.steps ?? []) {
    if (String(step.step_type || "action").toLowerCase() !== "action") continue;
    for (const clip of procedureStepVideoList(step.video)) {
      const item = procedureVideoToItem(clip);
      if (item) out.push(item);
    }
  }
  return out;
}

/** True when the answer is owned by video procedure evidence, not a PDF. */
export function isVideoOwnedProcedureAnswer(
  procedure?: RagProcedure | null,
  primaryKind?: string | null
): boolean {
  const sourceKind = String(procedure?.source_kind || "").toLowerCase();
  const kind = String(primaryKind || "").toLowerCase();
  return sourceKind === "video_primary" || kind === "video_guide";
}

/**
 * Zip worker action clips onto numbered Step Card items in document order.
 * Leaves existing `item.videos` in place. Compact / unnumbered items are skipped.
 */
export function bindProcedureClipsToNumberedItems(
  items: ProcedureBlockItem[],
  procedure: RagProcedure | null | undefined
): ProcedureBlockItem[] {
  const actionClips = procedureStepVideoItems(procedure);
  if (!actionClips.length || !items.length) return items;

  const next = items.map((item) => ({ ...item }));
  let clipIdx = 0;
  for (const item of next) {
    if (!item.num || clipIdx >= actionClips.length) continue;
    if (!item.videos?.length) {
      item.videos = [{ videoNum: clipIdx + 1, item: actionClips[clipIdx]! }];
    }
    clipIdx += 1;
  }
  return next;
}

/**
 * Attach worker per-step clips onto parsed Step Card items.
 * Action steps and numbered carousel items are zipped in document order —
 * both come from the same answer, so headings (compact cards) are skipped
 * on both sides. Does not use filenames or query text.
 */
export function attachProcedureStepVideos(
  blocks: ProcedureBlock[],
  procedure: RagProcedure | null | undefined
): ProcedureBlock[] {
  const actionClips = procedureStepVideoItems(procedure);
  if (!actionClips.length || !blocks.length) return blocks;

  const slots: Array<{ blockIdx: number; itemIdx: number }> = [];
  blocks.forEach((block, blockIdx) => {
    if (block.kind !== "steps") return;
    block.items.forEach((item, itemIdx) => {
      if (item.num) slots.push({ blockIdx, itemIdx });
    });
  });
  if (!slots.length) return blocks;

  const next = blocks.map((block) => ({
    ...block,
    items: block.items.map((item) => ({ ...item })),
  }));
  const n = Math.min(actionClips.length, slots.length);
  for (let i = 0; i < n; i++) {
    const { blockIdx, itemIdx } = slots[i]!;
    const item = next[blockIdx]!.items[itemIdx]!;
    if (item.videos?.length) continue;
    item.videos = [{ videoNum: i + 1, item: actionClips[i]! }];
  }
  return next;
}

const CALLOUT_STEP_TYPES = new Set(["warning", "caution", "note", "danger"]);

/** Layout role from the worker procedure contract (default = action). */
export function procedureStepLayoutKind(
  step: RagProcedureStep | null | undefined
): "action" | "section" | "callout" {
  const t = String(step?.step_type || "action").toLowerCase();
  if (t === "section") return "section";
  if (CALLOUT_STEP_TYPES.has(t)) return "callout";
  return "action";
}

/**
 * True when the worker procedure payload is the layout contract:
 * reliable, and at least two executable action steps.
 */
export function procedurePayloadIsLayoutSource(
  procedure: RagProcedure | null | undefined
): boolean {
  if (!procedure || procedure.steps_reliable === false) return false;
  let actions = 0;
  for (const step of procedure.steps || []) {
    if (procedureStepLayoutKind(step) === "action") actions += 1;
  }
  return actions >= 2;
}

function procedureStepCitePage(step: RagProcedureStep): string | null {
  const raw = step.source?.page ?? step.image?.page;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) return String(n);
  const s = String(raw).trim();
  return /^\d+$/.test(s) ? s : null;
}

function actionItemFromProcedureStep(
  step: RagProcedureStep,
  videoNum: number
): ProcedureBlockItem {
  const clips = procedureStepVideoList(step.video)
    .map((v) => procedureVideoToItem(v))
    .filter((item): item is RagVideoItem => Boolean(item));
  let text = String(step.text || "");
  const page = procedureStepCitePage(step);
  if (page && !/\[p\.\d+\]/.test(text)) {
    text = `${text} [p.${page}]`.trim();
  }
  return {
    num: String(step.step ?? videoNum),
    text,
    videos: clips.length
      ? clips.map((item, i) => ({ videoNum: videoNum + i, item }))
      : undefined,
  };
}

function isBareProseLeadHeading(title: string): boolean {
  const t = normalizeWiTitle(title);
  if (/^(purpose|overview|scope|introduction|summary)$/i.test(t)) return true;
  if (/^before\s+(?:you\s+)?begin$/i.test(t)) return true;
  if (/^before\s+starting$/i.test(t)) return true;
  if (/^prerequisites?$/i.test(t)) return true;
  if (/^when\s+to\s+use(?:\s+this\s+document)?$/i.test(t)) return true;
  return false;
}

function peelCompactFamilyHeading(text: string): { title: string; body: string } | null {
  const clean = stripInlineMarkdown(cleanStepDisplayText(text)).trim();
  if (!clean) return null;
  const dashSplit = clean.split(/\s*[-–—]\s+/);
  const head = (dashSplit[0] || "").trim();
  const afterDash = dashSplit.slice(1).join(" - ").trim();
  const tokens = head.split(/\s+/).filter(Boolean);
  let bestN = 0;
  let bestTitle = "";
  for (let n = 1; n <= Math.min(8, tokens.length); n++) {
    const candidate = tokens.slice(0, n).join(" ");
    const heading =
      isToolsSectionTitle(candidate) ||
      isSafetySectionTitle(candidate) ||
      isDocumentsSectionTitle(candidate) ||
      isStandaloneCautionSectionTitle(candidate) ||
      isWarningsNotesHeading(candidate) ||
      isBareProseLeadHeading(candidate);
    if (heading) {
      bestN = n;
      bestTitle = candidate;
      continue;
    }
    if (bestTitle) break;
  }
  if (!bestTitle) return null;
  const restOfHead = tokens.slice(bestN).join(" ").replace(/^[:.\-–—]\s*/, "");
  const body = [restOfHead, afterDash].filter(Boolean).join(" ").trim();
  return { title: bestTitle, body };
}

function extractLeadingAtxDocumentTitle(text: string): string | null {
  for (const line of String(text ?? "").split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (isVideoSourceProvenanceNotice(trimmed)) continue;
    if (isAtxMarkdownHeading(trimmed)) {
      return stripStepVideoReferences(sectionHeadingDisplayText(trimmed)) || null;
    }
    if (STEP_LINE.test(trimmed) || STEP_WORD.test(trimmed)) break;
  }
  return null;
}

/**
 * Build the procedure canvas from the worker `procedure` payload.
 *
 * Layout follows `step_type`. Only `section` starts a new group (compact
 * family or a new steps heading). Callouts stay in the current steps list —
 * they are item chrome, not document breaks. Markdown is only used for a
 * leading ATX title when the payload has no `title`. Returns null when the
 * payload is not a layout source.
 */
export function documentFromProcedurePayload(
  procedure: RagProcedure | null | undefined,
  answerText = ""
): ParsedProcedureDocument | null {
  if (!procedurePayloadIsLayoutSource(procedure) || !procedure) return null;

  const blocks: ProcedureBlock[] = [];
  let stepsHeading = "";
  let compactPending: { title: string; body: string } | null = null;
  let actions: ProcedureBlockItem[] = [];
  let actionVideoNum = 0;

  const emitCompact = (title: string, body: string) => {
    const classified = classifyProcedureBlock(title, body, []);
    const block: ProcedureBlock = {
      title,
      kind: classified.kind,
      compactVariant: classified.compactVariant,
      body: classified.body,
      items: classified.items,
    };
    if (!hasRenderableProcedureContent(block)) return;
    blocks.push(block);
  };

  const flushActions = () => {
    if (!actions.length) return;
    const groupTitle = stepsHeading ? stripStepVideoReferences(stepsHeading) : "";
    blocks.push({
      title: groupTitle,
      kind: "steps",
      body: "",
      items: actions,
      groupTitle: groupTitle || null,
    });
    actions = [];
  };

  const flushCompactPending = () => {
    if (!compactPending) return;
    emitCompact(compactPending.title, compactPending.body);
    compactPending = null;
  };

  for (const step of procedure.steps || []) {
    const kind = procedureStepLayoutKind(step);
    if (kind === "section") {
      flushActions();
      flushCompactPending();
      const peeled = peelCompactFamilyHeading(step.text || "");
      if (peeled) {
        if (
          isWarningsNotesHeading(peeled.title) ||
          procedureSectionKey(peeled.title) === "warnings-notes"
        ) {
          continue;
        }
        compactPending = peeled;
        continue;
      }
      const { title } = splitStepTitleBody(step.text || "");
      const heading =
        title || stripInlineMarkdown(cleanStepDisplayText(step.text || ""));
      if (!heading) continue;
      if (
        isWarningsNotesHeading(heading) ||
        procedureSectionKey(heading) === "warnings-notes"
      ) {
        continue;
      }
      stepsHeading = stripStepVideoReferences(heading);
      continue;
    }
    if (kind === "callout") {
      if (compactPending) flushCompactPending();
      const nClips = procedureStepVideoList(step.video).length;
      if (nClips) actionVideoNum += 1;
      actions.push(actionItemFromProcedureStep(step, actionVideoNum));
      continue;
    }
    if (compactPending) flushCompactPending();
    const nClips = Math.max(1, procedureStepVideoList(step.video).length);
    actions.push(actionItemFromProcedureStep(step, actionVideoNum + 1));
    actionVideoNum += nClips;
  }
  flushActions();
  flushCompactPending();

  const usable = blocks.filter(hasRenderableProcedureContent);
  if (!usable.some((b) => b.kind === "steps")) return null;

  const payloadTitle = String(procedure.title || "").trim();
  const stepGroups = Array.from(
    new Set(
      usable
        .filter((b) => b.kind === "steps" && b.groupTitle)
        .map((b) => String(b.groupTitle))
    )
  );
  // Umbrella title only. A name that is already a steps-carousel heading
  // (Removing / Installing / …) stays on that carousel with its step count.
  const documentTitle =
    payloadTitle && !stepGroups.includes(payloadTitle)
      ? payloadTitle
      : stepGroups.length === 0
        ? extractLeadingAtxDocumentTitle(answerText)
        : null;

  return {
    intro: null,
    documentTitle,
    blocks: usable,
  };
}

/** Videos not already shown on a step — the remaining rail. */
export function getUnreferencedVideos(
  answerText: string,
  videos: RagVideoItem[],
  procedure?: RagProcedure | null
): RagVideoItem[] {
  if (!videos.length) return [];
  const boundKeys = new Set(
    procedureStepVideoItems(procedure)
      .map((v) => videoFileKey(v.path))
      .filter(Boolean)
  );
  if (boundKeys.size) {
    return videos.filter((v) => !boundKeys.has(videoFileKey(v.path)));
  }
  // "(Video N)" only hides the rail when those ordinals sit on Step Cards.
  // Diagnostic bullets cite Video 1 with no card — keep the clip visible.
  if (!isProcedureAnswer(answerText)) return videos;
  const referenced = new Set(findReferencedVideoNumbers(answerText));
  if (!referenced.size) return videos;
  return videos.filter((_, idx) => !referenced.has(idx + 1));
}

/** Split prose into sentence-like chunks for preview truncation. */
export function splitIntoSentences(text: string): string[] {
  const clean = text.trim();
  if (!clean) return [];
  const parts = clean.match(/[^.!?]+[.!?]+|[^.!?]+$/g);
  return parts ? parts.map((s) => s.trim()).filter(Boolean) : [clean];
}

/** Collapse awkward line breaks and bullet dashes for preview sentence detection only. */
export function normalizeStepDescriptionForPreview(text: string): string {
  return text
    .replace(/\r\n/g, "\n")
    .replace(/\n+/g, " ")
    .replace(/\.\s*-\s+/g, ". ")
    .replace(/\s+-\s+(?=[A-Z])/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/** Join preview units with newlines when they represent list / multi-line content. */
function previewUnitSeparator(units: string[]): string {
  return units.some((u) => /^[-*•]/.test(u.trim())) ? "\n" : " ";
}

/** Parse description into complete sentences for card preview construction. */
export function getPreviewSentences(text: string): string[] {
  const raw = String(text ?? "").replace(/\r\n/g, "\n").trim();
  if (!raw) return [];

  // Keep nested bullet / multi-line step bodies as separate preview units so the
  // card can truncate between lines and offer Show more for the rest.
  const lines = raw
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length >= 2) {
    return lines;
  }

  const normalized = normalizeStepDescriptionForPreview(raw);
  if (!normalized) return [];
  return splitIntoSentences(normalized);
}

export type SentencePreviewOptions = {
  /** @deprecated Use getPreviewSentences + composeSentencePreview with DOM measurement. */
  maxChars?: number;
  maxSentences?: number;
};

/**
 * Compose a preview string from a chosen number of complete sentences.
 * Never cuts mid-sentence — caller decides how many sentences fit visually.
 */
export function composeSentencePreview(
  sentences: string[],
  visibleCount: number
): { preview: string; isTruncated: boolean } {
  if (!sentences.length || visibleCount <= 0) {
    return { preview: "", isTruncated: sentences.length > 0 };
  }

  const clamped = Math.min(visibleCount, sentences.length);
  const isTruncated = clamped < sentences.length;
  const joiner = previewUnitSeparator(sentences);
  const preview = sentences.slice(0, clamped).join(joiner);
  return {
    preview: isTruncated ? `${preview}${joiner === "\n" ? "\n" : " "}...` : preview,
    isTruncated,
  };
}

/**
 * Find the maximum number of leading sentences whose rendered text fits within `maxHeightPx`.
 * Uses a DOM element with matching typography — never truncates mid-sentence.
 */
export function measureVisibleSentenceCount(
  sentences: string[],
  measureEl: HTMLElement,
  maxHeightPx: number,
  containerWidthPx: number
): number {
  if (!sentences.length) return 0;

  measureEl.style.width = `${containerWidthPx}px`;
  const joiner = previewUnitSeparator(sentences);

  let fit = 0;
  for (let count = 1; count <= sentences.length; count += 1) {
    const text = sentences.slice(0, count).join(joiner);
    measureEl.textContent = text;
    if (measureEl.scrollHeight <= maxHeightPx) {
      fit = count;
    } else {
      break;
    }
  }

  return fit;
}

/**
 * Never return 0 when description sentences exist — avoids empty preview + Show more.
 * Measurement may return 0 when a single long sentence exceeds the budget; still show it.
 */
export function resolveVisibleSentenceCount(
  sentences: string[],
  measureEl: HTMLElement,
  maxHeightPx: number,
  containerWidthPx: number
): number {
  const fit = measureVisibleSentenceCount(sentences, measureEl, maxHeightPx, containerWidthPx);
  if (fit > 0 || !sentences.length) return fit;
  return 1;
}

/** @deprecated Prefer getPreviewSentences + measureVisibleSentenceCount in StepCard. */
export function buildSentenceAwarePreview(
  text: string,
  opts: SentencePreviewOptions = {}
): { preview: string; isTruncated: boolean } {
  const maxSentences = opts.maxSentences ?? 3;
  const sentences = getPreviewSentences(text);
  return composeSentencePreview(sentences, Math.min(maxSentences, sentences.length));
}

export type StepImageBinding = { img: RagImageItem; page: string };

/**
 * Assign images to step indices when the LLM omits [p.N] / [img:N] tags.
 * Keys are indices in the full steps array (including non-numbered lines).
 */
export function computeProportionalStepImages(
  steps: ParsedStep[],
  orderedQueue: QueuedInlineImage[]
): Record<number, StepImageBinding[]> {
  const hasImgTags = steps.some((s) => s.num && /\[img:\d+\]/.test(s.text));
  const hasPageCitations = steps.some((s) => s.num && /\[p\.\d+\]/.test(s.text));
  if (hasImgTags || hasPageCitations || orderedQueue.length === 0) {
    return {};
  }

  const numberedSteps = steps.filter((s) => s.num);
  const n = numberedSteps.length;
  const m = orderedQueue.length;
  const assignments: Record<number, StepImageBinding[]> = {};

  for (let qi = 0; qi < m; qi++) {
    const targetIdx = Math.max(0, Math.min(n - 1, Math.round(((qi + 0.5) / m) * n) - 1));
    const stepOrigIdx = steps.indexOf(numberedSteps[targetIdx]);
    if (stepOrigIdx >= 0) {
      if (!assignments[stepOrigIdx]) assignments[stepOrigIdx] = [];
      assignments[stepOrigIdx].push(orderedQueue[qi]);
    }
  }
  return assignments;
}

/** Bind inline/page-cited images to numbered step indices for card and catalog UIs. */
export function buildStepImagesByIndex(
  steps: ParsedStep[],
  inlineImages: Record<string, RagImageItem[]>
): Record<number, StepImageBinding[]> {
  const orderedQueue = buildOrderedImageQueue(inlineImages);
  const proportional = computeProportionalStepImages(steps, orderedQueue);
  let imgQueueIdx = 0;
  const perStep: Record<number, StepImageBinding[]> = {};

  steps.forEach((step, i) => {
    if (!step.num) return;
    const citedPages = Array.from(step.text.matchAll(/\[p\.(\d+)\]/g), (m) => m[1]);
    if (citedPages.length > 0) {
      perStep[i] = citedPages.flatMap((p) =>
        (inlineImages[p] ?? []).map((img) => ({ img, page: p }))
      );
    } else if (/\[img:\d+\]/.test(step.text) && imgQueueIdx < orderedQueue.length) {
      perStep[i] = [orderedQueue[imgQueueIdx++]];
    } else {
      perStep[i] = proportional[i] ?? [];
    }
  });

  return perStep;
}

/**
 * Images for the "More diagrams" collage — same source as {@link RagImageRail}:
 * top-level `images` not already cited inline when the answer contains [p.N] markers.
 * Deduped by path, sorted by page.
 */
export function selectMoreDiagramImages(
  images: RagImageItem[],
  inlineImages: Record<string, RagImageItem[]>,
  answerText: string
): RagImageItem[] {
  const hasInlineCitations = /\[p\.\d+\]/.test(answerText);
  const citedPaths = hasInlineCitations
    ? new Set(
        Object.values(inlineImages)
          .flat()
          .map((img) => img.path)
          .filter(Boolean)
      )
    : new Set<string>();

  const seen = new Set<string>();
  const uncited: RagImageItem[] = [];
  for (const img of images ?? []) {
    if (!img.path || citedPaths.has(img.path) || seen.has(img.path)) continue;
    seen.add(img.path);
    uncited.push(img);
  }

  return uncited.sort((a, b) => {
    const pa = Number(a.metadata?.page ?? 999);
    const pb = Number(b.metadata?.page ?? 999);
    return pa - pb;
  });
}

/** Flatten inlineImages in page order (deduped by path). */
export function flattenInlineDiagramImages(
  inlineImages: Record<string, RagImageItem[]>
): RagImageItem[] {
  const seen = new Set<string>();
  const result: RagImageItem[] = [];

  for (const { img } of buildOrderedImageQueue(inlineImages)) {
    if (!img.path || seen.has(img.path)) continue;
    seen.add(img.path);
    result.push(img);
  }

  for (const [page, list] of Object.entries(inlineImages)) {
    if (/^\d+$/.test(page)) continue;
    for (const img of list ?? []) {
      if (!img.path || seen.has(img.path)) continue;
      seen.add(img.path);
      result.push(img);
    }
  }

  return result;
}

export interface MainAndMoreDiagramSelection {
  mainDiagram: RagImageItem | null;
  moreDiagrams: RagImageItem[];
}

/**
 * Split diagrams into a single main image and a separate More Diagrams list.
 *
 * Main: first inline/cited image (numbered exploded-view when present), else first
 * uncited top-level image. More: additional unreferenced top-level images excluding
 * the main path. Never merges the two lists or duplicates the main image.
 */
export function selectMainAndMoreDiagramImages(
  images: RagImageItem[],
  inlineImages: Record<string, RagImageItem[]>,
  answerText: string
): MainAndMoreDiagramSelection {
  const inline = flattenInlineDiagramImages(inlineImages);
  const uncitedTop = selectMoreDiagramImages(images, inlineImages, answerText);

  const fallbackTop = (images ?? []).find((img) => Boolean(img.path)) ?? null;
  const mainDiagram = inline[0] ?? uncitedTop[0] ?? fallbackTop;
  const mainPath = mainDiagram?.path ?? "";

  const moreDiagrams = uncitedTop.filter((img) => img.path && img.path !== mainPath);

  return { mainDiagram: mainDiagram ?? null, moreDiagrams };
}

/** Parts catalog uses the same main + more split as specifications. */
export function selectPartsCatalogDiagramImages(
  images: RagImageItem[],
  inlineImages: Record<string, RagImageItem[]>,
  answerText: string
): MainAndMoreDiagramSelection {
  return selectMainAndMoreDiagramImages(images, inlineImages, answerText);
}
